import { VideoCallStats } from '@hyperconnect/media-metric';
import * as Sentry from '@sentry/nextjs';
import { IMessage } from '@stomp/stompjs';
import { AxiosResponse } from 'axios';
import { addDays, differenceInSeconds, isPast } from 'date-fns';
import { atom } from 'jotai';
import { atomWithReset, RESET, selectAtom } from 'jotai/utils';
import { queryClientAtom } from 'jotai-tanstack-query';
import { i18n } from 'next-i18next';
import { concatMap, exhaustMap } from 'rxjs/operators';

import appConfig from 'app-config';

import { BrokerEventTextChatReceived } from 'src/apis/brokerEvent/TextChatReceived';
import { DTOFileInfo } from 'src/apis/dto/DTOFileInfo';
import { DTOMatchReportRequest } from 'src/apis/dto/DTOMatchReportRequest';
import { REST_ERROR_CODES } from 'src/apis/exception';
import { isDefinedAxiosRESTFailure } from 'src/apis/utils';
import { getSttPermissionAcknowledged } from 'src/components/AzarOnly/AssistantConsent/utils';
import VirtualVideoErrorModal from 'src/components/AzarOnly/Match/Matching/VirtualVideoErrorModal';
import GemLackModal from 'src/components/GemLackModal';
import ItemLackModal from 'src/components/ItemLackModal';
import MatchTimeoutModal from 'src/components/MatchTimeoutModal';
import MatchStarterPackageNudgingModal from 'src/components/StarterPackageNudgingModal/MatchStarterPackageNudgingModal';
import SuspensionModal from 'src/components/SuspensionModal';
import { getCreatorViewModeEventName } from 'src/hooks/useCreatorViewMode';
import { FOLLOW_QUERY_KEYS } from 'src/hooks/useFollowQuery';
import { HISTORY_QUERY_KEYS } from 'src/hooks/useHistoryQuery';
import { PREFERRED_LANGUAGE_QUERY_KEYS } from 'src/hooks/usePreferredLanguagesQuery';
import {
  getInventoryAtom,
  remoteConfigAtom,
  userDataAtom,
  userInventoryAtom,
  userJoinDateAtom,
  stompBrokerInfoAtom,
} from 'src/stores/auth/atoms';
import { defaultCreatorFilterAppliedAtom, isCreatorModeAtom } from 'src/stores/creator/atom';
import { userEffectSettingAtom } from 'src/stores/deco/atom';
import {
  invalidateAndRefetchModelAtom,
  loadStaticFallbackAtom,
} from 'src/stores/downloadableContents/modelRegistry';
import { updateHistoryNotificationByMatchAtom } from 'src/stores/history/atom';
import {
  applyFallbackMLConfigAtom,
  antmanFeaturesCapturedAtom,
  antmanInferenceReportTimerAtom,
  antmanInferenceCountAtom,
  antmanInferenceQueueAtom,
  antmanLatencySumAtom,
  antmanResultAtom,
  humanVisionPipeAtom,
  hvmFeaturesCapturedAtom,
  hvmInferenceCountAtom,
  hvmInferenceQueueAtom,
  hvmInferenceReportTimerAtom,
  hvmLatencySumAtom,
  lidInferenceCountAtom,
  lidInferenceQueueAtom,
  lidInferenceReportTimerAtom,
  lidLatencySumAtom,
  lidModelConfigAtom,
  lidPipeAtom,
  resetHumanVisionStateAtom,
  startAntmanAtom,
  startHumanVisionAtom,
  startLidAtom,
} from 'src/stores/ml/atoms';
import { MLFeatureType, MLModelType } from 'src/stores/ml/types';
import { closeModalAtom, showModalAtom } from 'src/stores/modal/atoms';
import {
  afterMatchPopupCaptureDataAtom,
  handleAfterMatchPopupAtom,
} from 'src/stores/moderation/afterMatchPopupAtoms';
import {
  applyServerScreeningCooldownAtom,
  startServerScreeningAtom,
  stopServerScreeningAtom,
} from 'src/stores/moderation/serverScreeningAtoms';
import {
  handleChildDetectedReceivedAtom,
  handleScreeningReportedAtom,
  handleUnderageTriggerAtom,
  hvsScreeningCaptureAtom,
  underageTriggerContextAtom,
} from 'src/stores/moderation/underageAutoReportAtoms';
import { GetPreferredLanguagesResponse } from 'src/stores/profile/apis';
import { lastStompMessageAtom, stompAtom, stompCreatedLogAtom } from 'src/stores/stomp/atoms';
import { showToastAtom } from 'src/stores/toast/atoms';
import { featureSettingsAtom } from 'src/stores/user/atom';
import { UserSettingType } from 'src/stores/user/types';
import { Chat } from 'src/types/Chat';
import { EVENT_NAME, EVENT_TYPE } from 'src/types/Event';
import { GEM_LACK_TRIGGER } from 'src/types/gemLack';
import {
  MatchBrokerMessage,
  MatchConfig,
  MatchFilter,
  MatchFilterSelection,
  MatchInfo,
  MatchInfoMessage,
  MatchOptions,
  RandomMatchContext,
  ReportMatchMethod,
  SignalingInfo,
  STATUS,
  PreviousMatchSummary,
  ContentsMatchMessage,
} from 'src/types/Match';
import { ModalType } from 'src/types/Modal';
import { ToastType } from 'src/types/Toast';
import { ErrorResponse, PunishedException } from 'src/utils/api';
import { pushAppsflyerEvent } from 'src/utils/appsflyer/pba';
import { atomWithPipes, shareMap } from 'src/utils/atom';
import {
  getChatEventIcon,
  getChatEventItemCount,
  getChatEventMessageKey,
} from 'src/utils/chatEvent';
import { deepEqual, isObjectKeyTrue } from 'src/utils/common';
import { uploadContentManager } from 'src/utils/content-manager';
import { ControlledPromise } from 'src/utils/controlled-promise';
import getDeviceInfo, { isSafariOrIOSBrowser } from 'src/utils/device/info';
import { isAxiosError } from 'src/utils/error';
import { pushEvent } from 'src/utils/event-gateway';
import { formatEmojiText } from 'src/utils/formatter';
import {
  hasItemInInventory,
  MaybeRawInventoryItem,
  parseRawInventoryItems,
} from 'src/utils/itemInventory';
import { getAtomWithStorage } from 'src/utils/localStorageUtils';
import { createMatchUuid } from 'src/utils/match';
import AudioLevelGetter, { getTargetAudioLevel } from 'src/utils/media/audio-level';
import { dataURItoBlob, isImageFileEmpty } from 'src/utils/media/blob';
import { isValidCandidate } from 'src/utils/media/munge_sdp';
import MediaStreamPipeManager from 'src/utils/media/pipes';
import EffectRendererPipe from 'src/utils/media/pipes/effect';
import type LidAudioPipe from 'src/utils/media/pipes/lid';
import RotateMediaStream from 'src/utils/media/pipes/rotate';
import { SilentAudioPlayer } from 'src/utils/media/silent-audio-player';
import { clearStream } from 'src/utils/media/stream';
import { attachMediaElement, detachMediaElement } from 'src/utils/media/view';
import { sendDeviceInfo } from 'src/utils/metric';
import {
  getCurrentSnapshot,
  reportEncodeFps,
  reportSourceFps,
  startMatchWindow,
  startMirrorWindow,
} from 'src/utils/perf';
import { SpeechRecognizerManager, SttDataCollector } from 'src/utils/stt';
import { getUserGemsAmount } from 'src/utils/user';
import { PeerConnectionClient } from 'src/utils/webrtc';
import { printVideoCallStats } from 'src/utils/webrtc/bridge';
import { IceServerManager } from 'src/utils/webrtc/ice-server-manager';
import { MatchStat } from 'src/utils/webrtc/stat';

import {
  abortMatchAPI,
  CameraInMatchInfo,
  cancelMatchRandomAPI,
  listMatchFiltersAPI,
  matchRandomAPI,
  reportContentsMatchMetricsAPI,
  reportContentsMatchStartAPI,
  ReportMatchAPI,
  reportMatchFaceExposureAPI,
  reportMatchMetricsAPI,
  reportMatchStartAPI,
  sendTextChatAPI,
  sendWebRTCStatInfoV1API,
} from './apis';
import {
  MLMetricsAtom,
  clearHvmInferenceIntervalAtom,
  clearLidInferenceIntervalAtom,
  clearMLInferenceIntervalAtom,
  getReportIntervalMs,
  reportHvmInferencesAtom,
  reportLidInferencesAtom,
  reportMLAtom,
  reportAntmanInferenceAtom,
  reportMLMetricsAtom,
} from './mlReportAtoms';

export { MLMetricsAtom } from './mlReportAtoms';

export const recentMatchCountAtom = atom(0);

export const isShowingCoachMarkAtom = atom(false);

export const isVividiFilterEverOpenedAtom = getAtomWithStorage<boolean>(
  'isVividiFilterEverOpened',
  false
);

// Storage
export const matchFiltersAtom = getAtomWithStorage<MatchFilter[]>('matchFiltersV2', []);

export const getRequiredGemFromMatchFilterAtom = atom(
  null,
  (get, _set, updatedItems: MatchFilterSelection[]) => {
    const matchFilterSelections = updatedItems;
    const matchFilters = get(matchFiltersAtom);
    const userInventory = get(userInventoryAtom);

    const requiredGemAmount = matchFilterSelections.reduce((acc, selection) => {
      const { filterId, optionGemCost, optionKey } = selection;

      const filter = matchFilters.find((filterItem) => filterItem.filterId === filterId);

      const consumableCategoryId = filter?.consumableCategoryId;
      const periodicCategoryId = filter?.periodicCategoryId;

      // LOCAL_PAID는 서버에서 COUNTRY_GUARANTEE_FILTER_COUNT로 내려오지만
      // 실제 인벤토리 아이템은 LOCAL_GUARANTEE_COUNT이므로 별도 처리
      const hasLocalGuaranteeItem =
        optionKey === 'LOCAL_PAID' && hasItemInInventory(userInventory, 'LOCAL_GUARANTEE_COUNT');
      const canUseItem =
        (consumableCategoryId && hasItemInInventory(userInventory, consumableCategoryId)) ||
        (periodicCategoryId && hasItemInInventory(userInventory, periodicCategoryId));

      if (canUseItem || hasLocalGuaranteeItem) {
        return acc;
      }

      return acc + optionGemCost;
    }, 0);

    return requiredGemAmount;
  }
);

export const initialMatchFilterSelections: MatchFilterSelection[] = (() => {
  switch (appConfig.serviceCode) {
    case 'vividi':
      return [
        {
          filterId: 'GENDER_CHOICE',
          optionKey: 'ALL',
          optionGemCost: 0,
        },
      ];
    case 'azar':
    default:
      return [
        {
          filterId: 'GENDER_CHOICE',
          optionKey: 'ALL',
          optionGemCost: 0,
        },
        {
          filterId: 'REGION_CHOICE',
          optionKey: 'DEFAULT',
          optionGemCost: 0,
        },
      ];
  }
})();

export const matchFilterSelectionsAtom = getAtomWithStorage<MatchFilterSelection[]>(
  'matchFilterSelectionsV2',
  initialMatchFilterSelections
);

export const isPremiumAtom = selectAtom(
  matchFilterSelectionsAtom,
  (matchFilterSelections) => matchFilterSelections.some(({ optionGemCost }) => optionGemCost > 0),
  deepEqual
);
export const totalMatchTimeSecAtom = getAtomWithStorage('totalMatchTimeSecAtom', 0);

// stable
export const myVideoElsAtom = atom<HTMLCanvasElement[]>([]);
export const peerVideoElsAtom = atom<HTMLCanvasElement[]>([]);
export const matchOptionsAtom = getAtomWithStorage<MatchOptions>('matchOptions', {
  // PREFERRED_GENDER: 'ALL',
  PREFERRED_PLATFORM: null,
  MATCH_GROUP: 'DEFAULT',
  // preferredAge: 'BOTH',
  ...(process.env.NEXT_PUBLIC_ENVIRONMENT !== 'Prod' && { MATCH_TAG: null }),
});

// clear when leave page
export const statusAtom = atom(STATUS.INITIAL);
export const sourceStreamAtom = atom<MediaStream | null>(null); // 원본 media stream 보관

// createSourceStream 취소 요청 여부 (clearSourceStreamAtom 호출 시 true로 설정, 스트림 설정 반영 방지용)
const sourceStreamCancelledAtom = atom(false);

// clear when disconnect

export const localVideoToViewAtom = atom<MediaStreamPipeManager | null>(null); // local에 rendering할 stream
export const localVideoToSendAtom = atom<MediaStreamPipeManager | null>(null); // peer에게 전송할 local stream

export const addMyVideoElAtom = atom(null, (get, set, canvasEl: HTMLCanvasElement) => {
  const currentEls = get(myVideoElsAtom);
  if (!currentEls.includes(canvasEl)) {
    set(myVideoElsAtom, [...currentEls, canvasEl]);
    const localVideoToViewPipeManager = get(localVideoToViewAtom);
    if (localVideoToViewPipeManager) {
      localVideoToViewPipeManager.addOutputCanvas(canvasEl);
    }
  }
});

export const removeMyVideoElAtom = atom(null, (get, set, canvasEl: HTMLCanvasElement) => {
  const currentEls = get(myVideoElsAtom);
  if (currentEls.includes(canvasEl)) {
    set(
      myVideoElsAtom,
      currentEls.filter((el) => el !== canvasEl)
    );
    const localVideoToViewPipeManager = get(localVideoToViewAtom);
    if (localVideoToViewPipeManager) {
      localVideoToViewPipeManager.removeOutputCanvas(canvasEl);
    }
  }
});

export const localVideoStreamAtom = atom<MediaStream | null>(null);
export const isOnCameraAtom = atom(true);
export const peerVideoAtom = atom<MediaStreamPipeManager | null>(null); // peer로부터 받은 remote stream 을 transform
export const peerAudAtom = atom<HTMLAudioElement | null>(null); // peer로부터 받은 원본 audio track 보관
export const peerConnectionAtom = atom<PeerConnectionClient | null>(null);
export const matchInfoAtom = atom<MatchInfo | void>(undefined);
const sourceFpsTimerAtom = atom<ReturnType<typeof setInterval> | null>(null);
const sttRecognizerAtom = atom<SpeechRecognizerManager | null>(null);
const sttCollectorAtom = atom<SttDataCollector | null>(null);
const sttSucceedCountAtom = atom(0);
const sttSucceedLengthAtom = atom(0);
export const chatListAtom = atom<Chat[]>([]);
export const isPeerTypingAtom = atom<boolean>(false);
export const peerAudioLevelAtom = atom<number>(0);
export const peerAudioLevelGetterAtom = atom<AudioLevelGetter | void>(undefined);
export const localAudioLevelAtom = atom<number>(0);
export const localAudioLevelGetterAtom = atom<AudioLevelGetter | void>(undefined);
/**
 * 피어 비디오 첫 프레임 수신 여부
 *
 * - null : 매치 중이 아니므로, 상태 알 수 없음
 * - false : 피어 비디오 첫 프레임 수신 안됨
 * - true : 피어 비디오 첫 프레임 수신 됨
 *
 * frame을 받기 전 까지는 loading 표시를 위해, 상태로 관리합니다.
 */
export const isReceivedPeerVideoFirstFrameAtom = atom<boolean | null>(null);

export const addPeerVideoElAtom = atom(null, (get, set, canvasEl: HTMLCanvasElement) => {
  const currentEls = get(peerVideoElsAtom);
  if (!currentEls.includes(canvasEl)) {
    set(peerVideoElsAtom, [...currentEls, canvasEl]);
    const peerVideoPipeManager = get(peerVideoAtom);
    if (peerVideoPipeManager) {
      peerVideoPipeManager.addOutputCanvas(canvasEl);
    }
  }
});

export const removePeerVideoElAtom = atom(null, (get, set, canvasEl: HTMLCanvasElement) => {
  const currentEls = get(peerVideoElsAtom);
  if (currentEls.includes(canvasEl)) {
    set(
      peerVideoElsAtom,
      currentEls.filter((el) => el !== canvasEl)
    );
    const peerVideoPipeManager = get(peerVideoAtom);
    if (peerVideoPipeManager) {
      peerVideoPipeManager.removeOutputCanvas(canvasEl);
    }
  }
});

export const previousMatchSummaryAtom = atom<PreviousMatchSummary | null>(null);
export const matchConfigAtom = atom<MatchConfig | void>(undefined);
export const contentsMatchConfigAtom = atom<ContentsMatchMessage | null>(null);
export const isBonusMatchAtom = selectAtom(
  matchConfigAtom,
  (matchConfig) => !!matchConfig?.bonusMatchStatus?.bonusMatch
);
export const signalingInfoAtom = selectAtom(
  matchConfigAtom,
  (matchConfig) => matchConfig?.signalingInfo,
  deepEqual
);
export const isShowMatchFindingUIAtom = atom((get) => {
  const isContentsMatch = !!get(contentsMatchConfigAtom);

  // 컨텐츠 매치에서는 UI 상으로는 Finding과 동일하게 노출
  return get(statusAtom) === STATUS.FINDING || isContentsMatch;
});
export const matchStatAtom = atom<MatchStat | void>(undefined);
export const videoCallStatAtom = atomWithReset<VideoCallStats | void>(undefined);
export const debugStatAtom = selectAtom(videoCallStatAtom, (videoCallStat) => {
  if (!videoCallStat) return null;
  return printVideoCallStats(videoCallStat);
});
export const chatStartDateAtom = atom<Date | null>(null);
export const peerCameraEnabledAtom = atom<boolean>(true);
export const faceCountAtom = atomWithReset<number | undefined>(undefined);
const faceCountReportIndexAtom = atomWithReset(0);

export const matchRetryTimerAtom = atom<ReturnType<typeof setTimeout> | void>(undefined);
export const matchWaitTimerAtom = atom<ReturnType<typeof setTimeout> | void>(undefined);

export const nextMatchDisableShowAtom = atom(false);
export const endMatchDisableShowAtom = atom(false);

export const swipeDisableSecondAtom = atom(0);
export const swipeDisableDurationAtom = atom(0);
export const swipeDisabledTimerAtom = atom<ReturnType<typeof setInterval> | void>(undefined);

// api 서버에서 주는 matchId는 너무 길어서 서버의 matchId랑 matchUuid:matchId = 2(유저수):1 로 매핑되고 화면에 보여줄 uuid 매칭때마다 생성
export const matchUuidAtom = atom('');

const cameraInMatchInfoAtom = atom<CameraInMatchInfo | null>(null);
const lastCameraChangedTimeInMatchAtom = atom(0);

export const clearSwipeDisabledAtom = atom(null, (get, set) => {
  set(swipeDisableSecondAtom, 0);
  set(swipeDisableDurationAtom, 0);
  set(nextMatchDisableShowAtom, false);
  set(endMatchDisableShowAtom, false);
  const timerId = get(swipeDisabledTimerAtom);
  if (timerId) {
    clearInterval(timerId);
    set(swipeDisabledTimerAtom, undefined);
  }
});

const matchTimeoutCountInARowAtom = atom(0);

export const clearMatchRetryAtom = atom(null, (get, set) => {
  const timerId = get(matchRetryTimerAtom);
  set(matchTimeoutCountInARowAtom, 0);
  if (timerId) {
    clearTimeout(timerId);
    set(matchRetryTimerAtom, undefined);
  }
});

export const clearMatchWaitAtom = atom(null, (get, set) => {
  const timerId = get(matchWaitTimerAtom);
  if (timerId) {
    clearTimeout(timerId);
    set(matchWaitTimerAtom, undefined);
  }
});

const startDisableSecondAtom = atom(null, (get, set) => {
  const swipeDisableDuration = get(swipeDisableDurationAtom);
  if (!swipeDisableDuration) {
    set(clearSwipeDisabledAtom);
  } else if (swipeDisableDuration && !get(swipeDisabledTimerAtom)) {
    set(swipeDisableSecondAtom, swipeDisableDuration);
    set(
      swipeDisabledTimerAtom,
      setInterval(() => {
        const nextSecond = get(swipeDisableSecondAtom) - 1;
        if (nextSecond <= 0) {
          set(clearSwipeDisabledAtom);
        } else {
          set(swipeDisableSecondAtom, nextSecond);
        }
      }, 1000)
    );
  }
});

export const displayDelayTimerAtom = atom<ReturnType<typeof setTimeout> | void>(undefined);

const FALLBACK_SWIPE_DISABLE_DURATION_IN_CONNECTING = 7000;
export const matchWaitAtom = atom(null, (get, set) => {
  pushEvent({
    eventType: EVENT_TYPE.VIDEO_CHAT,
    eventName: EVENT_NAME.MATCH_FINDING__PROFILE,
  });
  const matchInfo = get(matchInfoAtom);
  const matchTimerId = setTimeout(() => {
    if (get(statusAtom) >= STATUS.CONNECTING) return;
    set(statusAtom, STATUS.CONNECTING);
    const matchConfig = get(matchConfigAtom);
    // ice connect 연결안되면 매치 재시도
    const timerId = setTimeout(
      () => {
        // eslint-disable-next-line @typescript-eslint/no-use-before-define
        set(handleDisconnectAtom, { reconnect: true, finisher: false });
      },
      Math.min(
        matchConfig?.iceConnectionTimeoutSeconds || 10,
        matchConfig?.matchTimeoutSeconds || 12
      ) * 1000
    );
    set(matchWaitTimerAtom, timerId);
    // connecting에서 프로필만 보고 스와이프 하는 것 막기
    const userData = get(userDataAtom);
    const swipeDisableDuration = get(isPremiumAtom)
      ? 0
      : Math.floor(
          (userData?.featureOptions.iceConnectNoSwipeDurationMs ??
            FALLBACK_SWIPE_DISABLE_DURATION_IN_CONNECTING) / 1000
        );
    set(swipeDisableDurationAtom, swipeDisableDuration);
    set(startDisableSecondAtom);
  }, matchInfo?.peerProfile.displayDelayMs || 0);
  set(displayDelayTimerAtom, matchTimerId);
});

export const clearDisplayDelayAtom = atom(null, (get, set) => {
  const timerId = get(displayDelayTimerAtom);
  if (timerId) {
    clearTimeout(timerId);
    set(displayDelayTimerAtom, undefined);
  }
});

const OLD_FREE_GENDER_OPTION = 'BOTH';
const NEW_FREE_GENDER_OPTION = 'ALL';
export const getMatchFiltersAtom = atom(null, async (get, set) => {
  try {
    const response = await listMatchFiltersAPI();
    const matchFilters = response.data.result.map((matchFilter) => {
      if (matchFilter.filterId !== 'REGION_CHOICE') {
        return matchFilter;
      }

      // 지역 필터에서 paid로 떨어지는 값들은 구버전 전용이라 미사용
      return {
        ...matchFilter,
        filterOptions: matchFilter.filterOptions.filter((filterOption) => {
          return !filterOption.paid;
        }),
      };
    });
    set(matchFiltersAtom, matchFilters);
    const matchFilterSelections = get(matchFilterSelectionsAtom);
    const genderFilter = matchFilters.find((filter) => filter.filterId === 'GENDER_CHOICE');
    const bothOption = genderFilter?.filterOptions.find(
      (option) => option.key === OLD_FREE_GENDER_OPTION
    );
    const allOption = genderFilter?.filterOptions.find(
      (option) => option.key === NEW_FREE_GENDER_OPTION
    );
    const genderFilterIndex = matchFilterSelections.findIndex(
      (filter) => filter.filterId === 'GENDER_CHOICE'
    );
    if (!genderFilter) return;
    // api 에서 가져온 값에 ALL 이 없는 경우 matchFilterSelections에 BOTH 추가
    if (!allOption && bothOption) {
      const newGenderOption = {
        filterId: 'GENDER_CHOICE',
        optionKey: bothOption.key,
        optionGemCost: bothOption.gemCost,
      } satisfies MatchFilterSelection;
      if (genderFilterIndex > -1) {
        // ALL 말고 이미 선택된 gender filter가 있는 경우 건너뛰기
        if (matchFilterSelections[genderFilterIndex].optionKey !== NEW_FREE_GENDER_OPTION) {
          return;
        }
        const updatedMatchFilterSelections = matchFilterSelections.map((selection) => {
          return selection.filterId === 'GENDER_CHOICE' ? newGenderOption : selection;
        });
        set(matchFilterSelectionsAtom, updatedMatchFilterSelections);
      } else {
        set(matchFilterSelectionsAtom, [...matchFilterSelections, newGenderOption]);
      }
    }
    // BOTH가 선택되어 있지만 api에서 ALL을 내려주는 경우 matchFilterSelections ALL로 변경
    if (
      genderFilterIndex > -1 &&
      matchFilterSelections[genderFilterIndex].optionKey === OLD_FREE_GENDER_OPTION &&
      !!allOption
    ) {
      const updatedMatchFilterSelections = matchFilterSelections.map((selection) => {
        return selection.filterId === 'GENDER_CHOICE'
          ? ({
              filterId: 'GENDER_CHOICE',
              optionKey: allOption.key,
              optionGemCost: allOption.gemCost,
            } satisfies MatchFilterSelection)
          : selection;
      });

      set(matchFilterSelectionsAtom, updatedMatchFilterSelections);
    }
  } catch (_e) {
    return;
  }
});

export const setupDefaultCreatorFilterSelectionAtom = atom(null, (get, set) => {
  const { isMobile } = getDeviceInfo();
  const remoteConfig = get(remoteConfigAtom);
  const enableCreatorPlan = remoteConfig && isObjectKeyTrue(remoteConfig, 'enableCreatorPlan');
  const faceOnMatchFilter = get(matchFiltersAtom).find(
    (filter) => filter.filterId === 'FACE_ON_MATCH'
  );
  // creator plan 최초 기본 필터설정
  if (
    enableCreatorPlan &&
    faceOnMatchFilter &&
    !isMobile &&
    !get(defaultCreatorFilterAppliedAtom)
  ) {
    set(matchFilterSelectionsAtom, (prev) => {
      const faceFilterOption = faceOnMatchFilter.filterOptions[0];
      if (!faceFilterOption) {
        return prev;
      }
      return [
        ...prev,
        {
          filterId: faceOnMatchFilter.filterId,
          optionKey: faceFilterOption.key,
          optionGemCost: faceFilterOption.gemCost,
        },
      ];
    });
    set(defaultCreatorFilterAppliedAtom, true);
  }
});

export const getMatchLackingGemAmountAtom = atom(
  null,
  (get, set, matchFilterSelections: MatchFilterSelection[] = get(matchFilterSelectionsAtom)) => {
    const requiredGemAmount = set(getRequiredGemFromMatchFilterAtom, matchFilterSelections);
    const inventoryItems = get(userInventoryAtom);
    const availableGemAmount = getUserGemsAmount(inventoryItems) || 0;
    return requiredGemAmount - availableGemAmount;
  }
);

/**
 * 매치 필터 조합을 기반으로 젬부족 사유를 추출
 * @params matchFilterSelections 검사하려는 매치 필터 조합 (default = 현재 적용된 매치 필터 목록)
 * @params isExcludeInventory 현재 보유중인 인벤토리를 배제하고 판단할 지 여부 (default = false)
 */
export const getGemLackTriggersFromMatchFiltersAtom = atom(
  null,
  (
    get,
    set,
    { matchFilterSelections = get(matchFilterSelectionsAtom), isExcludeInventory = false } = {}
  ) => {
    if (!isExcludeInventory) {
      const requiredGems = set(getMatchLackingGemAmountAtom, matchFilterSelections);

      // 인벤토리 확인해봤을 때 현재 인벤토리로 해당 필터 적용 가능한 경우
      if (requiredGems <= 0) return [];
    }

    const gemLackTriggers = matchFilterSelections
      .filter((selection) => {
        const requiredGem = isExcludeInventory
          ? selection.optionGemCost
          : set(getRequiredGemFromMatchFilterAtom, [selection]);
        return requiredGem > 0;
      })
      .map((selection) => {
        switch (selection?.filterId) {
          case 'GENDER_CHOICE':
            return GEM_LACK_TRIGGER.genderFilter;
          case 'REGION_CHOICE':
          case 'COUNTRY_GUARANTEE':
            return GEM_LACK_TRIGGER.locationFilter;
          default:
            return;
        }
      })
      .filter((gemlackTrigger) => gemlackTrigger !== undefined);

    return gemLackTriggers;
  }
);

export const stompSendAtom = atom(
  null,
  (
    get,
    set,
    message: string,
    signalingInfo: SignalingInfo | undefined = get(signalingInfoAtom)
  ) => {
    const stomp = get(stompAtom);
    if (!stomp || !signalingInfo) {
      return;
    }
    stomp.send({
      message,
      channelId: signalingInfo.channelId,
      clientId: signalingInfo.clientId,
    });
  }
);

const sendStompCameraStatusUpdatedAtom = atom(null, (get, set) => {
  if (get(statusAtom) !== STATUS.MATCHED) return;
  const isOnCamera = get(isOnCameraAtom);
  set(
    stompSendAtom,
    JSON.stringify({
      enabled: isOnCamera,
      matchId: get(matchInfoAtom)?.matchId,
      type: 'cameraStatusUpdated',
    })
  );
  const cameraInMatchInfo = get(cameraInMatchInfoAtom);
  const lastCameraChangedTimeInMatch = get(lastCameraChangedTimeInMatchAtom);
  const now = new Date().getTime();
  if (cameraInMatchInfo) {
    set(cameraInMatchInfoAtom, {
      ...cameraInMatchInfo,
      statusChangedTimeMs: [
        ...cameraInMatchInfo.statusChangedTimeMs,
        now - lastCameraChangedTimeInMatch,
      ],
    });
  } else {
    set(cameraInMatchInfoAtom, {
      activatedOnStart: isOnCamera,
      activatedOnEnd: isOnCamera,
      statusChangedTimeMs: [],
    });
  }
  set(lastCameraChangedTimeInMatchAtom, now);
});

interface AddChatEventMessageParams {
  updatedItems: MaybeRawInventoryItem[];
  prevGems: number;
}

const addChatEventMessageAtom = atom(
  null,
  (get, set, { updatedItems, prevGems }: AddChatEventMessageParams) => {
    const matchFilterSelections = get(matchFilterSelectionsAtom);
    const matchFilters = get(matchFiltersAtom);
    const userInventory = get(userInventoryAtom);

    const parsedUpdatedItems = parseRawInventoryItems(updatedItems);
    const updatedCategoryIds = new Set<string>(parsedUpdatedItems.map((i) => i.categoryId));

    const isPremium = get(isPremiumAtom);
    if (!isPremium) return;

    const addedCategoryIds = new Set<string>();

    const currentGems = getUserGemsAmount(userInventory) ?? 0;
    const isGemsDeducted = prevGems > currentGems;

    if (isGemsDeducted) {
      addedCategoryIds.add('GEMS');

      set(chatListAtom, (prev) => [
        ...prev,
        {
          type: 'remaining_item_message' as const,
          messageUid: `remaining-gems-${Date.now()}`,
          messageKey: 'match.toast_left_gem',
          messageParams: { gem: currentGems },
          iconUrl: '/images/icons/imgGemSquare.png',
        },
      ]);
    }

    matchFilterSelections.forEach((selection) => {
      if (selection.optionGemCost <= 0) return;

      const filter = matchFilters?.find((f) => f.filterId === selection.filterId);
      const filterCategoryId = filter?.consumableCategoryId;

      const isLocalPaid = selection.optionKey === 'LOCAL_PAID';
      const categoryId = isLocalPaid ? 'LOCAL_GUARANTEE_COUNT' : filterCategoryId;

      if (!categoryId) return;
      if (!updatedCategoryIds.has(categoryId)) return;
      if (addedCategoryIds.has(categoryId)) return;

      addedCategoryIds.add(categoryId);

      const remaining = getChatEventItemCount(userInventory ?? [], categoryId);
      const messageKeyInfo = getChatEventMessageKey(categoryId);

      if (messageKeyInfo) {
        set(chatListAtom, (prev) => [
          ...prev,
          {
            type: 'remaining_item_message' as const,
            messageUid: `remaining-${categoryId}-${Date.now()}`,
            messageKey: messageKeyInfo.key,
            messageParams: { [messageKeyInfo.paramName]: remaining },
            iconUrl: getChatEventIcon(categoryId),
          },
        ]);
      }
    });
  }
);

const handleUpdatedItemsAtom = atom(
  null,
  async (get, set, updatedItems: MaybeRawInventoryItem[]) => {
    const inventoryItems = get(userInventoryAtom);
    if (!inventoryItems || !updatedItems) {
      return;
    }
    const parsedUpdatedItems = parseRawInventoryItems(updatedItems);
    const nextUserInventory = [...inventoryItems];
    parsedUpdatedItems.forEach((updatedItem) => {
      const targetIndex = nextUserInventory.findIndex(
        ({ itemId }) => updatedItem.itemId === itemId
      );
      if (targetIndex === -1) {
        nextUserInventory.push(updatedItem);
      } else {
        nextUserInventory[targetIndex] = {
          ...nextUserInventory[targetIndex],
          quantity: updatedItem.quantity,
        };
      }
    });
    set(userInventoryAtom, nextUserInventory);
  }
);

export const reportMatchFaceCountAtom = atom(null, (get, set) => {
  /**
   * 한 매치에서 노출 얼굴 수를 보고할 수 있는 최대 횟수
   * 지나치게 많은 API 호출을 방지하기 위해 정의되었으며 앱과 동일한 기준으로 적용
   * (정량적 근거가 아닌 임의 합의값)
   */
  const MAX_REPORT_PER_MATCH = 30;

  const matchId = get(matchInfoAtom)?.matchId;
  const faceCount = get(faceCountAtom);
  const faceCountReportIndex = get(faceCountReportIndexAtom);

  if (
    faceCount !== undefined &&
    get(statusAtom) === STATUS.MATCHED &&
    matchId &&
    faceCountReportIndex < MAX_REPORT_PER_MATCH
  ) {
    reportMatchFaceExposureAPI({
      matchId,
      cameraEnabled: get(isOnCameraAtom),
      recognitions: [
        {
          faceCount,
          recognizedBy: 'mediapipe',
        },
      ],
    });
    set(faceCountReportIndexAtom, faceCountReportIndex + 1);
  }
});

const reportMatchStartAtom = atom(null, async (get, set) => {
  const matchInfo = get(matchInfoAtom);
  const matchStat = get(matchStatAtom);

  if (matchInfo) {
    const matchUuid = createMatchUuid();
    set(matchUuidAtom, matchUuid);
    const prevGems = getUserGemsAmount(get(userInventoryAtom)) ?? 0;
    try {
      const response = await reportMatchStartAPI({
        matchId: matchInfo.matchId,
        iceConnectTimeMs: matchStat?.getIceConnectTime() || undefined,
        acceptTimeMs: -1,
        matchContext: matchInfo.matchContext,
      });
      set(reportMatchFaceCountAtom);
      if (response?.data) {
        const {
          data: {
            result: { updatedItems },
          },
        } = response;
        await set(handleUpdatedItemsAtom, updatedItems);
        await set(getInventoryAtom);
        set(addChatEventMessageAtom, { updatedItems, prevGems });
      } else {
        Sentry.captureMessage(
          `Match Start's result field is null. matchId: ${matchInfo.matchId} json: ${response.data}`
        );
        await set(getInventoryAtom);
      }
    } catch (err) {
      Sentry.captureException(err);
    }
    set(sendStompCameraStatusUpdatedAtom);
    pushEvent({
      eventType: EVENT_TYPE.VIDEO_CHAT,
      eventName: EVENT_NAME.MATCH_UUID,
      eventParams: { match_uuid: matchUuid, match_id: matchInfo.matchId },
    });

    const fs = get(featureSettingsAtom);
    const assistantConsented = fs?.find(
      (s) => s.name === UserSettingType.ASSISTANT_CONSENTED
    )?.value;
    const assistantEnabled = fs?.find((s) => s.name === UserSettingType.ASSISTANT_ENABLED)?.value;

    // 매치 종료 후, 프로메테우스관련 STT 데이트 이벤트로 전송
    pushEvent({
      eventType: EVENT_TYPE.PROMETHEUS,
      eventName: EVENT_NAME.MIRROR__CHECK_ASSISTANT_CONSENTED,
      eventParams: {
        action_category: 'action',
        tab: 'mirror',
        page: 'match',
        target: 'assistant_consented',
        match_id: matchInfo.matchId,
        device_language: navigator.language,
        stt_language: matchInfo.sttLanguageCode,
        status: String(assistantConsented ?? null),
      },
    });
    pushEvent({
      eventType: EVENT_TYPE.PROMETHEUS,
      eventName: EVENT_NAME.MIRROR__CHECK_ASSISTANT_ENABLED,
      eventParams: {
        action_category: 'action',
        tab: 'mirror',
        page: 'match',
        target: 'assistant_enabled',
        match_id: matchInfo.matchId,
        device_language: navigator.language,
        stt_language: matchInfo.sttLanguageCode,
        status: String(assistantEnabled ?? null),
      },
    });
    const sttPermissionGranted = isSafariOrIOSBrowser() || getSttPermissionAcknowledged() === true;
    const deviceSttEnabled =
      assistantConsented === true && assistantEnabled === true && sttPermissionGranted;

    pushEvent({
      eventType: EVENT_TYPE.PROMETHEUS,
      eventName: EVENT_NAME.MIRROR__CHECK_STT_STATUS,
      eventParams: {
        action_category: 'action',
        tab: 'mirror',
        page: 'match',
        target: 'stt_status',
        match_id: matchInfo.matchId,
        device_language: navigator.language,
        stt_language: matchInfo.sttLanguageCode,
        device_stt_status: String(deviceSttEnabled),
        match_info_status: String(!!matchInfo.sttLanguageCode),
      },
    });
    pushEvent({
      eventType: EVENT_TYPE.PROMETHEUS,
      eventName: EVENT_NAME.MIRROR__CHECK_STT_SUBTITLES_ENABLED,
      eventParams: {
        action_category: 'action',
        tab: 'mirror',
        page: 'match',
        target: 'stt_subtitles_enabled',
        match_id: matchInfo.matchId,
        device_language: navigator.language,
        stt_language: matchInfo.sttLanguageCode,
        status: String(deviceSttEnabled),
      },
    });
  }
});

const isMatchAcceptedAtom = atom(false);

const matchSwipeDisableDurationSecondsAtom = atom((get) => {
  const FALLBACK_SWIPE_DISABLE_DURATION_MS_IN_MATCH = 5000;

  const userData = get(userDataAtom);

  // 일반적인 케이스에 해당 유저가 아무 옵션 없이 매치를 돌렸을 때 적용되는 대기 시간
  const defaultUserSwipeDisableDurationMs =
    userData?.featureOptions.swipeDisabledDurationMs ?? FALLBACK_SWIPE_DISABLE_DURATION_MS_IN_MATCH;

  // 이번 매치에 적용되는 모든 조건들을 고려하여 서버에서 내려준 대기 시간
  const currentMatchSwipeDisableDurationMs = get(matchInfoAtom)?.swipeDisabledDurationMs;

  const durationMs = currentMatchSwipeDisableDurationMs ?? defaultUserSwipeDisableDurationMs;
  return Math.floor(durationMs / 1000);
});

const handleStartAtom = atom(null, (get, set) => {
  set(clearMatchWaitAtom);

  pushEvent({
    eventType: EVENT_TYPE.VIDEO_CHAT_DEBUG,
    eventName: EVENT_NAME.DEBUG__MATCH_LOGGING,
    eventParams: {
      debug: 'enter handleStartAtom',
      matchInfo: get(matchInfoAtom),
      isMatchAcceptedAtom,
    },
  });

  // 오디오가 있다면 항상 먼저 unmute 시도
  const audio = get(peerAudAtom);
  if (audio) {
    audio.muted = false;
  }

  // ice connected랑 first frame, match accepted 순서 보장 안됨
  if (
    get(statusAtom) === STATUS.MATCHED ||
    !get(matchStatAtom)?.getIceConnectTime() ||
    !get(isMatchAcceptedAtom)
  )
    return;

  const peerConnection = get(peerConnectionAtom);
  peerConnection?.enableTracks();

  set(chatStartDateAtom, new Date());

  pushEvent({
    eventType: EVENT_TYPE.VIDEO_CHAT_DEBUG,
    eventName: EVENT_NAME.DEBUG__MATCH_LOGGING,
    eventParams: {
      debug: 'call set(statusAtom, STATUS.MATCHED)',
    },
  });
  set(statusAtom, STATUS.MATCHED);
  // 매치화면으로 넘어간 이후 과금되도록 나중에 실행
  set(reportMatchStartAtom);

  // connecting에서 실행한 swipe disable를 clear
  set(clearSwipeDisabledAtom);
  set(swipeDisableDurationAtom, get(matchSwipeDisableDurationSecondsAtom));
  set(startDisableSecondAtom);

  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  set(startSttCollectionAtom);
});

const startSttCollectionAtom = atom(null, (get, set) => {
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  set(stopSttCollectionAtom);

  const matchInfo = get(matchInfoAtom);
  if (!matchInfo) return;

  const { sttLanguageCode, matchId } = matchInfo;
  if (!sttLanguageCode) return;

  const featureSettings = get(featureSettingsAtom);
  const isConsented =
    featureSettings?.find((s) => s.name === UserSettingType.ASSISTANT_CONSENTED)?.value === true;
  const isEnabled =
    featureSettings?.find((s) => s.name === UserSettingType.ASSISTANT_ENABLED)?.value === true;
  if (!isConsented || !isEnabled) return;

  // Safari 또는 iOS 브라우저인 경우, STT 비활성화된 경우 미수집
  // (safari 계정이 아닌 경우, 음성인식 시스템 권한이 없기 때문에, localStorage 기반의 권한을 사용)
  if (!isSafariOrIOSBrowser() && getSttPermissionAcknowledged() !== true) return;

  set(sttSucceedCountAtom, 0);
  set(sttSucceedLengthAtom, 0);

  const collector = new SttDataCollector(matchId, sttLanguageCode);
  const recognizer = new SpeechRecognizerManager(
    { lang: sttLanguageCode, continuous: true },
    {
      onSegmentComplete: (segment) => {
        collector.add(segment);
        set(sttSucceedCountAtom, (c) => c + 1);
        set(sttSucceedLengthAtom, (l) => l + segment.text.length);
        pushEvent({
          eventType: EVENT_TYPE.PROMETHEUS,
          eventName: EVENT_NAME.MIRROR__SUCCEED_STT,
          eventParams: {
            action_category: 'action',
            tab: 'mirror',
            page: 'match',
            target: 'stt',
            device_language: navigator.language,
            stt_language: sttLanguageCode,
            match_id: matchId,
            stt_length: segment.text.length,
          },
        });
      },
      onError: (error) => {
        pushEvent({
          eventType: EVENT_TYPE.PROMETHEUS,
          eventName: EVENT_NAME.MIRROR__FAIL_STT,
          eventParams: {
            action_category: 'action',
            tab: 'mirror',
            page: 'match',
            target: 'stt',
            device_language: navigator.language,
            stt_language: sttLanguageCode,
            match_id: matchId,
            error_code: String(error.code),
          },
        });
      },
      onStateChange: (active) => {
        if (!active) {
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          set(stopSttCollectionAtom);
        }
      },
    }
  );

  set(sttCollectorAtom, collector);
  set(sttRecognizerAtom, recognizer);

  collector.startPeriodicSend();

  pushEvent({
    eventType: EVENT_TYPE.PROMETHEUS,
    eventName: EVENT_NAME.MIRROR__ATTEMPT_STT,
    eventParams: {
      action_category: 'action',
      tab: 'mirror',
      page: 'match',
      target: 'stt',
      device_language: navigator.language,
      stt_language: sttLanguageCode,
      match_id: matchId,
    },
  });

  recognizer.start();
});

const stopSttCollectionAtom = atom(null, (get, set) => {
  const recognizer = get(sttRecognizerAtom);
  if (recognizer) {
    set(sttRecognizerAtom, null);
    recognizer.stop();
  }

  const collector = get(sttCollectorAtom);
  if (collector) {
    set(sttCollectorAtom, null);
    collector.stop();
  }

  const succeedCount = get(sttSucceedCountAtom);
  const succeedLength = get(sttSucceedLengthAtom);
  if (collector && succeedCount > 0) {
    pushEvent({
      eventType: EVENT_TYPE.PROMETHEUS,
      eventName: EVENT_NAME.MIRROR__COUNT_SUCCEED_STT,
      eventParams: {
        action_category: 'action',
        tab: 'mirror',
        page: 'match',
        target: 'stt',
        device_language: navigator.language,
        stt_language: collector.languageCode,
        match_id: collector.matchId,
        succeed_count: succeedCount,
        stt_length: succeedLength,
      },
    });
  }
  set(sttSucceedCountAtom, 0);
  set(sttSucceedLengthAtom, 0);
});

export const handleVirtualVideoErrorAtom = atom(null, (get, set, label: string) => {
  set(showModalAtom, {
    key: ModalType.ERROR,
    component: () => VirtualVideoErrorModal({ label }),
  });
});

export const receiveChatAtom = atom(
  null,
  (get, set, chat: Pick<BrokerEventTextChatReceived, 'originalMessage' | 'messageUid'>) => {
    const matchInfo = get(matchInfoAtom);

    const { originalMessage: message, messageUid = `${Date.now()}` } = chat;

    /**
     * 매치 중 아님 || 메시지 없음 || messageUid 가 null 인 경우는 없지만 그냥 type guard 처리
     */
    if (!matchInfo?.peerProfile || message == null) {
      return;
    }

    set(chatListAtom, (prev) =>
      prev.concat([
        {
          type: 'azar_message',
          messageUid,
          message,
          sender: matchInfo.peerProfile,
          sentByMe: false,
        },
      ])
    );
  }
);

export const sendChatAtom = atom(null, async (get, set, message: string) => {
  const userData = get(userDataAtom);
  const matchInfo = get(matchInfoAtom);
  if (!userData?.userProfile || !matchInfo?.matchId || !message) {
    return;
  }
  const { matchId } = matchInfo;
  const { userProfile } = userData;
  const {
    data: {
      result: { messageUid = `${Date.now()}` },
    },
  } = await sendTextChatAPI({
    matchId,
    message,
  });

  set(chatListAtom, (prev) =>
    prev.concat([
      {
        type: 'azar_message',
        message,
        messageUid,
        sender: userProfile,
        sentByMe: true,
      },
    ])
  );
});

export const startMatchAtom = atomWithPipes(
  null,
  async (get, set) => {
    pushEvent({
      eventType: EVENT_TYPE.VIDEO_CHAT_DEBUG,
      eventName: EVENT_NAME.DEBUG__MATCH_LOGGING,
      eventParams: {
        debug: 'startMatchAtom',
      },
    });
    const stomp = get(stompAtom);
    if (!stomp) {
      Sentry.captureMessage('There is no stomp connection when startMatch2', {
        extra: {
          log: get(stompCreatedLogAtom),
          hasStompBrokerInfo: !!get(stompBrokerInfoAtom),
        },
      });

      pushEvent({
        eventType: EVENT_TYPE.LOG,
        eventName: EVENT_NAME.ANYWHERE__REPORT_MATCH_REJECTED,
        eventParams: {
          reason: 'no_stomp',
        },
      });
      return;
    }
    if (get(matchInfoAtom)) {
      Sentry.captureMessage('startMatchAtom is called with another match');

      pushEvent({
        eventType: EVENT_TYPE.LOG,
        eventName: EVENT_NAME.ANYWHERE__REPORT_MATCH_REJECTED,
        eventParams: {
          reason: 'other_match_in_progress',
        },
      });
      return;
    }
    const matchOptions = get(matchOptionsAtom);
    const matchFilterSelections = get(matchFilterSelectionsAtom);
    // onMeteredNetwork: PeerProfile.maxVideoBandwidthKbps 에 영향을 줍니다.
    // TODO(daniel.l): Browser 에서 사용 가능한 API 가 있는지 탐색
    const onMeteredNetwork = false;
    // deviceAvailableVideoHeight: 해당 기기에서 원활하게 인코딩/디코딩 할 수 있는 최대 세로 길이입니다.
    // TODO(daniel.l): Browser 에서 측정 가능한 방법이 있는지 확인
    const deviceAvailableVideoHeight = 720;
    const randomMatchContext: RandomMatchContext = {
      onMeteredNetwork,
      deviceAvailableVideoHeight,
      ca: get(isOnCameraAtom),
    };
    const matchEffectIds: string[] = [];

    const matchStat = new MatchStat();
    matchStat.beginProfileTime = Date.now();
    const userEffectSetting = get(userEffectSettingAtom);

    try {
      set(statusAtom, STATUS.FINDING);
      pushEvent({
        eventType: EVENT_TYPE.VIDEO_CHAT_DEBUG,
        eventName: EVENT_NAME.DEBUG__MATCH_LOGGING,
        eventParams: {
          debug: 'matchRandomAPI start',
          request: {
            matchOptions,
            matchEffectIds,
            userEffectSetting,
            randomMatchContext,
            matchFilterSelections,
            previousMatchSummary: get(previousMatchSummaryAtom) ?? undefined,
          },
        },
      });
      const {
        data: { result },
      } = await matchRandomAPI({
        matchOptions,
        matchEffectIds,
        userEffectSetting,
        randomMatchContext,
        matchFilterSelections,
        previousMatchSummary: get(previousMatchSummaryAtom) ?? undefined,
      });

      const preferredLanguagesData = get(queryClientAtom).getQueryData<
        AxiosResponse<GetPreferredLanguagesResponse>
      >(PREFERRED_LANGUAGE_QUERY_KEYS.getPreferredLanguages());

      const preferredLanguages =
        preferredLanguagesData?.data.preferredLanguages.map(
          (preferredLanguage) => preferredLanguage.code
        ) ?? [];

      pushEvent({
        eventType: EVENT_TYPE.FILTER,
        eventName: EVENT_NAME.MIRROR__REPORT_PREFER_LANGUAGE_INFO,
        eventParams: {
          action_category: 'report',
          tab: 'mirror',
          page: 'main',
          target: 'prefer_language_info',
          selected: preferredLanguages,
          request_id: result.requestId,
        },
      });
      pushEvent({
        eventType: EVENT_TYPE.VIDEO_CHAT_DEBUG,
        eventName: EVENT_NAME.DEBUG__MATCH_LOGGING,
        eventParams: {
          debug: 'matchRandomAPI success',
          response: result,
        },
      });
      // 매치 요청 응답이 오기 전에 미러로 이동한 경우
      if (get(statusAtom) === STATUS.INITIAL) {
        pushEvent({
          eventType: EVENT_TYPE.LOG,
          eventName: EVENT_NAME.ANYWHERE__REPORT_MATCH_REJECTED,
          eventParams: {
            reason: 'back_to_mirror_tab_before_matched',
          },
        });

        pushEvent({
          eventType: EVENT_TYPE.VIDEO_CHAT_DEBUG,
          eventName: EVENT_NAME.DEBUG__MATCH_LOGGING,
          eventParams: {
            debug: 'matchRandomAPI success but status is INITIAL',
          },
        });
        // 타이밍 이슈에 따라 매치가 생성될 수 있음
        // 생성된 매치의 상대 유저가 타임아웃까지 대기하는 것을 막기 위해 BYE만 전송
        set(stompSendAtom, 'BYE', result.signalingInfo);
        return;
      }
      const sourceStream = get(sourceStreamAtom);
      const isOnCamera = get(isOnCameraAtom);
      if (sourceStream) {
        const localAudioLevelGetter = new AudioLevelGetter({
          stream: sourceStream,
          callback: (audioLevel) => set(localAudioLevelAtom, getTargetAudioLevel(audioLevel)),
        });
        if (!isOnCamera) {
          localAudioLevelGetter.start();
        }
        set(localAudioLevelGetterAtom, localAudioLevelGetter);
      }

      set(matchConfigAtom, result);
      set(matchStatAtom, matchStat);
      const { channelId, clientId } = result.signalingInfo;
      const headers = {
        selector: `sender-id <> '${clientId}'`,
      };

      stomp.subscribe({
        channelId,
        onMessage: (message) =>
          // eslint-disable-next-line @typescript-eslint/no-use-before-define
          set(handleMatchStompMessageAtom, {
            message,
            channelId,
          }),
        headers,
      });
      set(
        matchRetryTimerAtom,
        setTimeout(
          () => {
            set(matchTimeoutCountInARowAtom, (prev) => prev + 1);
            pushEvent({
              eventType: EVENT_TYPE.VIDEO_CHAT_DEBUG,
              eventName: EVENT_NAME.DEBUG__MATCH_LOGGING,
              eventParams: {
                debug: 'matchRetryTimerAtom timeout',
                isOnline: window.navigator.onLine,
                lastStompMessage: get(lastStompMessageAtom),
                channelId,
                matchInfo: get(matchInfoAtom),
                matchTimeoutCountInARow: get(matchTimeoutCountInARowAtom),
              },
            });
            if (appConfig.serviceCode === 'vividi') {
              set(showModalAtom, {
                key: ModalType.MATCH_TIMEOUT,
                component: MatchTimeoutModal,
              });
              // eslint-disable-next-line @typescript-eslint/no-use-before-define
              set(handleDisconnectAtom, { reconnect: false, finisher: false });
            } else {
              // eslint-disable-next-line @typescript-eslint/no-use-before-define
              set(handleDisconnectAtom, { reconnect: true, finisher: false });
            }
          },
          (get(matchConfigAtom)?.matchTimeoutSeconds || 30) * 1000
        )
      );
    } catch (error) {
      if (
        isDefinedAxiosRESTFailure(error, REST_ERROR_CODES.NO_ITEM) &&
        appConfig.serviceCode === 'vividi'
      ) {
        const controlledPromise = new ControlledPromise<'Confirm' | 'Close' | 'Dismiss'>();
        set(showModalAtom, {
          key: ModalType.ITEM_LACK,
          component: () =>
            ItemLackModal({
              onConfirm: () => controlledPromise.resolve('Confirm'),
              onClose: () => controlledPromise.resolve('Close'),
              onDismiss: () => controlledPromise.resolve('Dismiss'),
            }),
        });
        controlledPromise.promise.then((action) => {
          if (action === 'Confirm') {
            // eslint-disable-next-line @typescript-eslint/no-use-before-define
            set(handleDisconnectAtom, { reconnect: false, finisher: false });
          } else {
            set(matchFilterSelectionsAtom, initialMatchFilterSelections);
            if (action === 'Dismiss') {
              // eslint-disable-next-line @typescript-eslint/no-use-before-define
              set(handleDisconnectAtom, { reconnect: true, finisher: false });
            } else {
              // eslint-disable-next-line @typescript-eslint/no-use-before-define
              set(handleDisconnectAtom, { reconnect: false, finisher: false });
            }
          }
        });
        return;
      }

      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      set(clearMatchAtom);
      pushEvent({
        eventType: EVENT_TYPE.VIDEO_CHAT_DEBUG,
        eventName: EVENT_NAME.DEBUG__MATCH_LOGGING,
        eventParams: {
          debug: 'startMatchAtom error',
          error,
        },
      });
      if (!isAxiosError<ErrorResponse<PunishedException>>(error)) {
        pushEvent({
          eventType: EVENT_TYPE.LOG,
          eventName: EVENT_NAME.ANYWHERE__REPORT_MATCH_REJECTED,
          eventParams: {
            reason: 'match_request_fail_no_axios_error',
            raw: error,
          },
        });

        return;
      }
      const errorBody = error?.response?.data?.error?.data;
      if (!errorBody) {
        pushEvent({
          eventType: EVENT_TYPE.LOG,
          eventName: EVENT_NAME.ANYWHERE__REPORT_MATCH_REJECTED,
          eventParams: {
            reason: 'match_request_fail_no_error_body',
            raw: error,
          },
        });
        return;
      }
      switch (error?.response?.status) {
        case 403: {
          pushEvent({
            eventType: EVENT_TYPE.LOG,
            eventName: EVENT_NAME.ANYWHERE__REPORT_MATCH_REJECTED,
            eventParams: {
              reason: 'match_suspension',
              raw: errorBody,
            },
          });

          set(showModalAtom, {
            key: ModalType.MATCH_SUSPENSION,
            component: () =>
              SuspensionModal({
                punishment: errorBody,
              }),
          });
          break;
        }
        case 402: {
          pushEvent({
            eventType: EVENT_TYPE.LOG,
            eventName: EVENT_NAME.ANYWHERE__REPORT_MATCH_REJECTED,
            eventParams: {
              reason: 'gem_lack_in_server',
              raw: errorBody,
            },
          });

          set(closeModalAtom, ModalType.MATCH_SETTING);
          set(showModalAtom, {
            key: ModalType.GEM_LACK,
            component: () =>
              GemLackModal({
                lackingGemAmount: set(getMatchLackingGemAmountAtom),
                gemLackTriggers: set(getGemLackTriggersFromMatchFiltersAtom),
              }),
          });

          break;
        }
        case 400: {
          pushEvent({
            eventType: EVENT_TYPE.LOG,
            eventName: EVENT_NAME.ANYWHERE__REPORT_MATCH_REJECTED,
            eventParams: {
              reason: 'request_error_400',
              raw: errorBody,
            },
          });

          set(matchFilterSelectionsAtom, initialMatchFilterSelections);
          set(getMatchFiltersAtom);
          break;
        }
        default: {
          pushEvent({
            eventType: EVENT_TYPE.LOG,
            eventName: EVENT_NAME.ANYWHERE__REPORT_MATCH_REJECTED,
            eventParams: {
              reason: 'match_request_error_default',
              raw: errorBody,
            },
          });
          Sentry.captureMessage('MatchRandomAPI unhandled error', {
            extra: { error },
          });
        }
      }
    }
  },
  [exhaustMap((x) => x())]
);

export const myVideoAspectRatioAtom = atom(1);
export const handleMyStreamVideoAtom = atomWithPipes(
  null,
  async (get, set) => {
    const sourceStream = get(sourceStreamAtom);

    if (!sourceStream) return;

    const myVideoEls = get(myVideoElsAtom);

    const sourceVideo = document.createElement('video');
    const videoTracks = sourceStream.getVideoTracks();
    const { width = 0, height = 0 } = videoTracks[0].getSettings();
    set(myVideoAspectRatioAtom, width / height);
    const videoOnlyStream = new MediaStream(videoTracks);
    set(localVideoStreamAtom, videoOnlyStream);
    attachMediaElement(sourceVideo, videoOnlyStream);

    // 무조건 새로운 MediaStreamPipeManager 생성
    const newMyVideoStreamPipeManager = new MediaStreamPipeManager();
    newMyVideoStreamPipeManager.videoFpsSource = 'local';
    newMyVideoStreamPipeManager.setEl(sourceVideo, myVideoEls);

    const effectStreamPipe = new EffectRendererPipe();
    newMyVideoStreamPipeManager.pipe(effectStreamPipe);
    newMyVideoStreamPipeManager.start();

    const prevLocalVideoToViewAtom = get(localVideoToViewAtom);
    if (prevLocalVideoToViewAtom) {
      prevLocalVideoToViewAtom.dispose();
    }
    set(localVideoToViewAtom, newMyVideoStreamPipeManager);
    set(closeModalAtom, ModalType.ERROR);
  },
  [concatMap((x) => x())]
);

const bannedVideoInputList = new RegExp(
  [
    'OBS',
    'XSplit',
    'Many',
    'Snap',
    'Chroma',
    'CamMask',
    'Split',
    'Alter',
    'Virtual',
    'Streamlabs',
    'VCam',
  ].join('|')
);

const validateVideoDeviceLabel = atom(null, (get, _set, stream: MediaStream) => {
  const videoDeviceLabel = stream.getVideoTracks()[0]?.label;
  const remoteConfig = get(remoteConfigAtom);
  if (!remoteConfig || !isObjectKeyTrue(remoteConfig, 'enableOBS')) {
    if (bannedVideoInputList.test(videoDeviceLabel)) {
      const err = new Error(videoDeviceLabel);
      err.name = 'VirtualCameraError';
      throw err;
    }
  }
});

type GrantVideoParams = {
  onSuccess?: () => void;
  onError?: (err: Error) => void;
  permissionErrorModal?: React.FC;
};

const updateSourceStreamIfCamera = atom(null, async (get, set) => {
  const sourceStream = get(sourceStreamAtom);
  if (get(isOnCameraAtom) && sourceStream && !sourceStream?.getVideoTracks()[0]?.enabled) {
    const audioTrack = sourceStream.getAudioTracks()[0];
    clearStream(sourceStream, { audio: false, video: true });
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: true,
      });
      stream.addTrack(audioTrack);
      if (get(sourceStreamCancelledAtom)) {
        clearStream(stream);
        return;
      }
      set(sourceStreamAtom, stream);
    } catch (_err) {
      const error = new Error();
      error.name = 'NotAllowedError';
      throw error;
    }
  }

  if (get(isOnCameraAtom)) {
    await set(handleMyStreamVideoAtom);
  }
});

const createSourceStream = atom(null, async (get, set) => {
  set(sourceStreamCancelledAtom, false);

  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: true,
      video: true,
    });
    if (get(sourceStreamCancelledAtom)) {
      clearStream(stream);
      return;
    }

    set(validateVideoDeviceLabel, stream);
    set(sourceStreamAtom, stream);
  } catch (_err) {
    const error = new Error();
    error.name = 'NotAllowedError';
    throw error;
  }

  if (get(isOnCameraAtom)) {
    await set(handleMyStreamVideoAtom);
  }
});

const handleSourceStream = atomWithPipes<null, []>(
  null,
  async (get, set) => {
    const sourceStream = get(sourceStreamAtom);
    if (sourceStream) {
      await set(updateSourceStreamIfCamera);
    } else {
      await set(createSourceStream);
    }
  },
  [shareMap((x) => x())]
);

export const isGrantVideoPendingAtom = atom(false);

enum MediaDevicePermissionState {
  None = 'none',
  Granted = 'granted',
  Denied = 'denied',
}

const mediaDevicePermissionStateAtom = getAtomWithStorage(
  'mediaDevicePermission',
  MediaDevicePermissionState.None
);

const updateMediaDevicePermissionStateAtom = atom(
  null,
  (get, set, state: MediaDevicePermissionState) => {
    const prevState = get(mediaDevicePermissionStateAtom);
    if (prevState === state) return;

    pushEvent({
      eventType: EVENT_TYPE.PERMISSION,
      eventName: EVENT_NAME.ANYWHERE__REPORT_PERMISSION_STATUS,
      eventParams: {
        action_category: 'report',
        tab: 'anywhere',
        page: 'anywhere',
        target: 'permission_status',
        from_status: prevState,
        to_status: state,
      },
    });

    set(mediaDevicePermissionStateAtom, state);
  }
);

export const grantVideoAtom = atom(null, async (get, set, params: GrantVideoParams = {}) => {
  if (!navigator?.mediaDevices?.getUserMedia) {
    Sentry.captureMessage('getUserMedia를 지원하지 않는 브라우저입니다.');
    set(showToastAtom, {
      type: ToastType.ERROR,
      message: i18n?.t('POPUP_NOT_SUPPORTED_DESC') || '',
    });
    params.onError?.(new Error('no getUserMedia'));
    return;
  }
  set(isGrantVideoPendingAtom, true);
  try {
    await set(handleSourceStream);
    params.onSuccess?.();
    set(updateMediaDevicePermissionStateAtom, MediaDevicePermissionState.Granted);
  } catch (err) {
    params.onError?.(err as Error);

    if ((err as Error).name === 'VirtualCameraError') {
      set(handleVirtualVideoErrorAtom, (err as Error).message);
    } else if ((err as Error).name === 'NotAllowedError') {
      set(updateMediaDevicePermissionStateAtom, MediaDevicePermissionState.Denied);
      if (params.permissionErrorModal) {
        set(showModalAtom, {
          key: ModalType.ERROR,
          component: params.permissionErrorModal,
        });
      }
    }
  } finally {
    set(isGrantVideoPendingAtom, false);
  }

  const stream = get(sourceStreamAtom);
  if (stream) {
    sendDeviceInfo(stream);
  }
});

export const clearSourceStreamAtom = atom(null, (get, set) => {
  set(sourceStreamCancelledAtom, true);

  const sourceStream = get(sourceStreamAtom);
  if (sourceStream) {
    clearStream(sourceStream);
    set(sourceStreamAtom, null);
    const localVideoToView = get(localVideoToViewAtom);
    localVideoToView?.dispose(true);
    set(localVideoToViewAtom, null);
    set(localVideoStreamAtom, null);
  }
});

export const startFindingMatchAtom = atom(null, (get, set) => {
  set(statusAtom, STATUS.FINDING);
  pushEvent({
    eventType: EVENT_TYPE.VIDEO_CHAT,
    eventName: EVENT_NAME.SCREEN_OPENED__MATCH_FINDING,
  });
  set(startMatchAtom);
});

// report modal open 시에 video, audio 차단
export const isBlockedPeerVideoAtom = atom(false);
export const blockPeerMediaAtom = atom(null, (get, set, isBlock: boolean) => {
  const peerAud = get(peerAudAtom);
  if (peerAud) {
    peerAud.muted = isBlock;
  }
  set(isBlockedPeerVideoAtom, isBlock);
});
export const closeMatchReportModalAtom = atom(null, (get, set) => {
  set(closeModalAtom, ModalType.REPORT);
  set(blockPeerMediaAtom, false);
});

const handleAhaMomentEventAtom = atom(null, (get, set) => {
  const userJoinDate = get(userJoinDateAtom);
  const chatStartDate = get(chatStartDateAtom);
  const matchDuration = chatStartDate ? differenceInSeconds(new Date(), chatStartDate) : 0;
  const prevTotalMatchDuration = get(totalMatchTimeSecAtom);
  const nowTotalMatchDuration = prevTotalMatchDuration + matchDuration;
  set(totalMatchTimeSecAtom, nowTotalMatchDuration);
  if (!userJoinDate) return;
  const userJoinAfterOneWeek = addDays(userJoinDate, 7);
  if (isPast(userJoinAfterOneWeek)) return;
  if (nowTotalMatchDuration < 600) {
    return;
  }
  if (prevTotalMatchDuration < 600 && 600 <= nowTotalMatchDuration) {
    pushEvent({
      eventType: EVENT_TYPE.VIDEO_CHAT,
      eventName: EVENT_NAME.AHAMOMENT_MATCH_10MIN,
      eventParams: { duration: matchDuration },
    });
    pushAppsflyerEvent('match_10min');
    return;
  }
  if (prevTotalMatchDuration < 1200 && 1200 <= nowTotalMatchDuration) {
    pushEvent({
      eventType: EVENT_TYPE.VIDEO_CHAT,
      eventName: EVENT_NAME.AHAMOMENT_MATCH_20MIN,
      eventParams: { duration: matchDuration },
    });
    pushAppsflyerEvent('match_20min');
    return;
  }
  if (prevTotalMatchDuration < 1800 && 1800 <= nowTotalMatchDuration) {
    pushEvent({
      eventType: EVENT_TYPE.VIDEO_CHAT,
      eventName: EVENT_NAME.AHAMOMENT_MATCH_30MIN,
      eventParams: { duration: matchDuration },
    });
    pushAppsflyerEvent('match_30min');
    return;
  }
});

const reportMatchResultAtom = atom(
  null,
  async (get, set, { reconnect, finisher }: { reconnect: boolean; finisher: boolean }) => {
    if (get(statusAtom) !== STATUS.MATCHED) return;
    const matchInfo = get(matchInfoAtom);
    const matchId = matchInfo?.matchId;
    if (!matchId) return;

    const chatStartDate = get(chatStartDateAtom) || undefined;
    const matchStat = get(matchStatAtom);
    const videoCallStat = get(videoCallStatAtom);
    const peerConnection = get(peerConnectionAtom);
    const webrtcMatchStats = {
      ...(peerConnection?.getWebRTCMatchStats() || {}),
      peerProfileTimeMs: matchStat?.getPeerProfileTimeMs(),
    };
    const localCandidateType = videoCallStat?.localCandidateType || undefined;
    const remoteCandidateType = videoCallStat?.remoteCandidateType || undefined;
    const iceConnectTimeMs = matchStat?.getIceConnectTime() || undefined;
    const cameraInMatchInfo = get(cameraInMatchInfoAtom);
    const isOnCamera = get(isOnCameraAtom);
    // NOTE(daniel.l): reportMatchMetrics 의 iceConnectTimeMs 는 deprecate 되었다는데,
    // 확인 후 추후 제거
    const chatDurationMs = chatStartDate && new Date().getTime() - chatStartDate.getTime();
    const antmanFeatures = get(antmanFeaturesCapturedAtom);
    const antmanMatchBlurCaptured = antmanFeatures[MLFeatureType.MatchBlur];
    const eventObj = {
      eventType: EVENT_TYPE.VIDEO_CHAT,
      eventName: reconnect ? EVENT_NAME.MATCHING__NEXT : EVENT_NAME.MATCHING__EXIT,
      eventParams: {
        reconnect,
        finisher,
        ...(!!antmanMatchBlurCaptured && { screen: 'blur' }),
      },
    };

    set(updateHistoryNotificationByMatchAtom, {
      matchDurationMs: chatDurationMs ?? 0,
    });

    const matchConfig = get(matchConfigAtom);
    if (matchConfig?.shouldReportMatchSummary) {
      set(previousMatchSummaryAtom, {
        matchId,
        chatDurationMs: chatDurationMs ?? 0,
        finisher,
        bonusMatch: !!matchConfig?.bonusMatchStatus?.bonusMatch,
      });
    } else {
      set(previousMatchSummaryAtom, null);
    }

    Promise.all([
      reportMatchMetricsAPI({
        matchUid: matchId,
        initiator: matchInfo?.initiator,
        chatDurationMs,
        finisher,
        firstFrameReceivedTimeMs: matchStat?.getFirstFrameReceivedTimeMs() || undefined,
        localCandidateType,
        remoteCandidateType,
        iceConnectTimeMs,
        ...(cameraInMatchInfo && {
          cameraInMatchInfo: {
            ...cameraInMatchInfo,
            activatedOnEnd: isOnCamera,
          },
        }),
        /*
      facialBlurOnWhenLogin: undefined,
      facePositions: undefined,
      numRecognizedFace: undefined,
      */
      }),
      sendWebRTCStatInfoV1API({
        matchId,
        stats: webrtcMatchStats,
      }),
      pushEvent(eventObj),
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      set(pushMatchViewModeEvent, { matchId, chatDurationMs }),
    ]).catch((e) => {
      // metric 집계 에러
      Sentry.captureException(e);
    });
  }
);

const reportContentsMatchResultAtom = atom(null, (get) => {
  if (get(statusAtom) !== STATUS.CONTENTS_MATCH) return;
  const contentsMatchConfig = get(contentsMatchConfigAtom);
  if (!contentsMatchConfig) return;

  const chatStartDate = get(chatStartDateAtom);
  const contentsMatchPlayTimeMs = chatStartDate
    ? new Date().getTime() - chatStartDate.getTime()
    : 0;
  reportContentsMatchMetricsAPI({
    contentsMatchId: contentsMatchConfig.contentsMatchId,
    contentsMatchPlayTimeMs,
  });
});

export const reportBeforeUnloadAtom = atom(null, (get, set) => {
  const matchId = get(matchInfoAtom)?.matchId;
  set(stopSttCollectionAtom);
  set(reportMLAtom, false);
  set(reportMatchResultAtom, {
    reconnect: false,
    finisher: true,
  });
  set(reportMLMetricsAtom, matchId);
  set(reportContentsMatchResultAtom);
});

export const handleDisconnectAtom = atomWithPipes<
  null,
  [{ reconnect: boolean; finisher: boolean }]
>(
  null,
  async (get, set, { reconnect, finisher }) => {
    pushEvent({
      eventType: EVENT_TYPE.VIDEO_CHAT_DEBUG,
      eventName: EVENT_NAME.DEBUG__MATCH_LOGGING,
      eventParams: {
        debug: 'handleDisconnectAtom',
        reconnect,
        finisher,
      },
    });

    const status = get(statusAtom);
    const stomp = get(stompAtom);
    set(handleAhaMomentEventAtom);
    set(matchUuidAtom, '');
    if (status === STATUS.INITIAL) {
      return;
    }

    const matchInfo = get(matchInfoAtom);
    const signalingInfo = get(signalingInfoAtom);
    if (stomp && signalingInfo) {
      stomp.unsubscribe(signalingInfo.channelId);
    }
    if (finisher) {
      set(stompSendAtom, 'BYE');
    }

    set(clearMLInferenceIntervalAtom);
    set(clearHvmInferenceIntervalAtom);
    set(clearLidInferenceIntervalAtom);
    set(stopServerScreeningAtom);

    set(stopSttCollectionAtom);

    const matchId = matchInfo?.matchId;
    const contentsMatchConfig = get(contentsMatchConfigAtom);

    set(reportMLAtom, reconnect);

    // 조건 판단 + 데이터 추출 (cleanup 전에 수행 — cleanup 후 atom이 RESET됨)
    const matchConfig = get(matchConfigAtom);
    const afterMatchCaptureData = get(afterMatchPopupCaptureDataAtom);
    const popupContext = matchId &&
      matchConfig &&
      afterMatchCaptureData && {
        matchId,
        requestId: matchConfig.requestId,
        reconnect,
        captureData: afterMatchCaptureData,
      };

    try {
      // 컨텐츠 매치 시작한 상태
      if (contentsMatchConfig && status === STATUS.CONTENTS_MATCH) {
        set(reportContentsMatchResultAtom);
      } else if (!matchId && signalingInfo) {
        await cancelMatchRandomAPI();
      } else if (finisher && matchId && status === STATUS.CONNECTING) {
        const {
          data: {
            result: { updatedItems },
          },
        } = await abortMatchAPI({ matchId });
        set(handleUpdatedItemsAtom, updatedItems);
      } else if (matchId && status === STATUS.MATCHED) {
        set(reportMatchResultAtom, { reconnect, finisher });
      }
    } catch (error) {
      // API 성공 여부와 별개로 매치를 이탈할 수 있도록 관련 요소 이어서 cleanup
      Sentry.captureException(error, {
        extra: {
          matchId,
          status,
          reconnect,
          finisher,
        },
      });
    }

    if (!reconnect) {
      set(statusAtom, STATUS.INITIAL);
      get(localAudioLevelGetterAtom)?.stop();
      set(localAudioLevelGetterAtom, undefined);
    }

    set(clearDisplayDelayAtom);
    set(clearMatchWaitAtom);
    set(clearMatchRetryAtom);
    set(clearSwipeDisabledAtom);
    get(peerAudioLevelGetterAtom)?.stop();
    set(peerAudioLevelGetterAtom, undefined);
    set(peerAudioLevelAtom, 0);
    set(chatListAtom, []);
    set(matchInfoAtom, undefined);
    set(matchConfigAtom, undefined);
    set(contentsMatchConfigAtom, null);
    set(matchStatAtom, undefined);
    set(videoCallStatAtom, RESET);
    set(isMatchAcceptedAtom, false);
    const peerAud = get(peerAudAtom);
    if (peerAud) {
      detachMediaElement(peerAud);
      set(peerAudAtom, null);
    }
    const peerVideo = get(peerVideoAtom);
    set(peerCameraEnabledAtom, true);
    if (peerVideo) {
      peerVideo.dispose(true);
      set(peerVideoAtom, null);
    }
    const humanVisionPipe = get(humanVisionPipeAtom);
    if (humanVisionPipe) {
      humanVisionPipe.dispose();
      set(humanVisionPipeAtom, null);
    }
    const lidPipe = get(lidPipeAtom);
    if (lidPipe) {
      lidPipe.dispose();
      set(lidPipeAtom, null);
    }
    set(resetHumanVisionStateAtom);
    set(underageTriggerContextAtom, RESET);
    set(hvsScreeningCaptureAtom, null);
    set(chatStartDateAtom, null);
    set(closeMatchReportModalAtom);
    set(closeModalAtom, ModalType.PROFILE_MODAL);
    set(faceCountReportIndexAtom, RESET);
    const localVideoToSend = get(localVideoToSendAtom);
    if (localVideoToSend) {
      localVideoToSend.dispose();
      set(localVideoToSendAtom, null);
    }
    set(antmanFeaturesCapturedAtom, RESET);
    set(antmanResultAtom, RESET);
    set(cameraInMatchInfoAtom, null);
    set(lastCameraChangedTimeInMatchAtom, 0);
    set(isReceivedPeerVideoFirstFrameAtom, null);
    const sfTimer = get(sourceFpsTimerAtom);
    if (sfTimer) {
      clearInterval(sfTimer);
      set(sourceFpsTimerAtom, null);
    }

    const peerConnection = get(peerConnectionAtom);
    if (peerConnection) {
      peerConnection.close();
      set(peerConnectionAtom, null);
    }

    if (matchId || getCurrentSnapshot()?.kind === 'match') {
      startMirrorWindow();
    }

    get(queryClientAtom).removeQueries({ queryKey: HISTORY_QUERY_KEYS.cards() });

    // after match popup 표시: cleanup 완료 + 화면 전환 후
    if (popupContext) {
      if (reconnect) {
        set(statusAtom, STATUS.FINDING);
      }
      await set(handleAfterMatchPopupAtom, popupContext);
    }

    set(reportMLMetricsAtom, matchId);
    set(MLMetricsAtom, []);

    if (reconnect) {
      if (!popupContext) {
        set(statusAtom, STATUS.FINDING);
      }
      await set(startMatchAtom);
    }
  },
  [concatMap((x) => x())]
);

export const validateImageAndReportMatchAtom = atom(
  null,
  async (
    get,
    set,
    {
      file,
      matchId,
      reportType,
      reportMethod,
    }: Pick<DTOMatchReportRequest, 'matchId' | 'reportType' | 'reportMethod'> & {
      file: Blob | null;
    }
  ) => {
    let fileInfo: DTOFileInfo | undefined;

    if (file) {
      const isCaptureImageEmpty = await isImageFileEmpty(file).catch((err) => {
        Sentry.captureException(err);
      });
      if (isCaptureImageEmpty) {
        let remoteInboundRtpStreamStats = {};

        const peerConnection = get(peerConnectionAtom);
        const stats = await peerConnection?.getStats();
        if (stats) {
          stats.forEach((stat) => {
            if (stat.type === 'remote-inbound-rtp') {
              remoteInboundRtpStreamStats = stat;
            }
          });
        }
        Sentry.captureMessage(`Report image captured but empty`, {
          extra: {
            remoteInboundRtpStreamStats,
          },
        });
      } else if (isCaptureImageEmpty === false) {
        /**
         * - MPaaS 이미지 신고시 최대한 rich한 이미지를 요구하므로 jpeg resize 적용하지 않음
         *  - webrtc로 전송되는 peer frame은 1440p 미만으로 MPaaS 사이즈 이슈 없음
         *  - https://hpcnt.slack.com/archives/C02DG93S9PF/p1776741703219369
         *  - mirror 신고 시 확인 필요
         * - 혹시나 이미지 파일 업로드 실패하더라도 신고 API 호출하도록 catch
         */
        fileInfo = await uploadContentManager(file, true).catch(() => undefined);
      }
    }

    await ReportMatchAPI({
      matchId,
      reportType,
      fileInfo,
      reportMethod,
    });
  }
);

const reportMatchStatusAtom = atom(false);
export const reportMatchAtom = atom(
  null,
  async (
    get,
    set,
    {
      reportType,
      reportMethod,
      file,
    }: Pick<DTOMatchReportRequest, 'reportType' | 'reportMethod'> & {
      file: Blob | null;
    }
  ) => {
    if (get(reportMatchStatusAtom)) {
      return;
    }
    set(reportMatchStatusAtom, true);
    const matchId = get(matchInfoAtom)?.matchId;
    if (!matchId) return;

    try {
      await set(validateImageAndReportMatchAtom, {
        file,
        matchId,
        reportType,
        reportMethod,
      });
      set(showToastAtom, {
        message: 'REPORT_COMPLETED',
        type: ToastType.SUCCESS,
      });
      await set(handleDisconnectAtom, { reconnect: true, finisher: true });
    } catch (e) {
      set(showToastAtom, {
        message: 'REPORT_FAILED',
        type: ToastType.ERROR,
      });
    } finally {
      set(closeMatchReportModalAtom);
      set(reportMatchStatusAtom, false);
    }
  }
);

export const closeAntmanWarningAtom = atom(null, async (get, set, shouldReport) => {
  const antmanFeatures = get(antmanFeaturesCapturedAtom);
  const antmanMatchBlurCaptured = antmanFeatures[MLFeatureType.MatchBlur];
  if (!antmanMatchBlurCaptured) {
    return;
  }
  pushEvent({
    eventType: EVENT_TYPE.VIDEO_CHAT,
    eventName: shouldReport ? EVENT_NAME.BLUR__REPORT_AND_LEAVE : EVENT_NAME.BLUR__CONTINUE_MATCH,
  });
  const { file, triggeredAt } = antmanMatchBlurCaptured;
  set(
    MLMetricsAtom,
    get(MLMetricsAtom).concat([
      {
        triggeredAt,
        action: shouldReport ? 'reported' : 'dismissed',
        feature: 'MatchBlur',
        modelType: MLModelType.Antman,
        actedAt: new Date().getTime(),
      },
    ])
  );
  if (shouldReport) {
    await set(reportMatchAtom, {
      reportType: 'VISUAL_ABUSE',
      reportMethod: ReportMatchMethod.IN_MATCH_ON_BLUR,
      file,
    });
  }
  // antmanFeaturesCapturedAtom의 MatchBlur handled 업데이트
  set(antmanFeaturesCapturedAtom, {
    ...get(antmanFeaturesCapturedAtom),
    [MLFeatureType.MatchBlur]: { ...antmanMatchBlurCaptured, handled: true },
  });
});

export const clearMatchAtom = atomWithPipes(
  null,
  async (get, set) => {
    await set(handleDisconnectAtom, { reconnect: false, finisher: true });
    /**
     * PG 결제의 특성 상 결제 완료 -> 승인 및 재화 지급은 비동기적으로 동작
     * 관련 타이밍 이슈로 매치 중 구매 시 매치 시작 API에서 획득한 인벤토리 정보와 구매 상품 지급으로 인한 브로커 메시지의 인벤토리가 충돌할 수 있음
     * 미러 등에서의 최소한 정합성 보장을 위해 매치 플로우를 이탈할 때 명시적으로 인벤토리를 최신화
     */
    await set(getInventoryAtom);
    SilentAudioPlayer.Instance.stop();
  },
  [shareMap((x) => x())]
);

export const attachTracksAtom = atom(null, (get, set) => {
  const myVideoEls = get(myVideoElsAtom);
  if (myVideoEls.length === 0) return;

  const peerConnection = get(peerConnectionAtom);
  const sourceStream = get(sourceStreamAtom);
  const localVideoToView = get(localVideoToViewAtom);

  if (!localVideoToView?.mediaStream || !sourceStream) {
    return;
  }

  const video = document.createElement('video');
  attachMediaElement(video, localVideoToView.mediaStream);
  const localVideoToSend = new MediaStreamPipeManager();
  localVideoToSend.videoFpsSource = 'local-send';
  localVideoToSend.setEl(video);
  localVideoToSend.pipe(new RotateMediaStream(90, true));
  const { mediaStream: transformVideoStream, width, height } = localVideoToSend.start();

  set(localVideoToSendAtom, localVideoToSend);
  if (width && height) {
    peerConnection?.setInputVideoSize(width, height);
  }

  const audioTracks = sourceStream.getAudioTracks() || [];
  const videoTracks = transformVideoStream.getVideoTracks() || [];
  [...audioTracks, ...videoTracks].forEach((track) => {
    track.enabled = false; // matchAccept 이후 pc.enableTracks로 활성화
    peerConnection?.addTrack(track);
  });
});

const handleIceCandidateAtom = atom(null, (_get, set, e: RTCPeerConnectionIceEvent) => {
  if (!e.candidate) {
    return;
  }

  const candidateStr = e.candidate.candidate;
  if (candidateStr === '') {
    return;
  } else {
    if (!isValidCandidate(candidateStr)) {
      Sentry.captureMessage('Invalid candidate created', {
        extra: {
          sdpMid: e.candidate.sdpMid,
          sdpMLineIndex: e.candidate.sdpMLineIndex,
          candidate: candidateStr,
        },
      });
      return;
    }
  }

  const candidate = {
    id: e.candidate.sdpMid,
    type: 'candidate',
    candidate: candidateStr,
    label: e.candidate.sdpMLineIndex,
  };
  set(stompSendAtom, JSON.stringify(candidate));
});

const handleTrackAtom = atom(null, (get, set, e: RTCTrackEvent) => {
  const matchInfo = get(matchInfoAtom);
  if (!matchInfo) {
    return;
  }

  const {
    peerProfile: { hasChoice },
    matchId,
  } = matchInfo;

  let el: HTMLMediaElement | null = null;

  pushEvent({
    eventType: EVENT_TYPE.VIDEO_CHAT_DEBUG,
    eventName: EVENT_NAME.DEBUG__MATCH_LOGGING,
    eventParams: {
      debug: 'handleTrackAtom',
      trackKind: e.track.kind,
    },
  });

  if (e.track.kind === 'audio') {
    const peerAud = get(peerAudAtom);
    el = peerAud || document.createElement('audio');
    if (!peerAud) {
      set(peerAudAtom, el);
    }
    const stream = new MediaStream([e.track]);
    attachMediaElement(el, stream);
    el.muted = true;
    try {
      const peerAudioLevelGetter = new AudioLevelGetter({
        stream,
        callback: (audioLevel) => {
          const peerAudioLevel = getTargetAudioLevel(audioLevel);
          set(peerAudioLevelAtom, peerAudioLevel);
        },
      });
      if (get(peerCameraEnabledAtom)) {
        peerAudioLevelGetter.start();
      }
      set(peerAudioLevelGetterAtom, peerAudioLevelGetter);
      pushEvent({
        eventType: EVENT_TYPE.VIDEO_CHAT_DEBUG,
        eventName: EVENT_NAME.DEBUG__MATCH_LOGGING,
        eventParams: {
          debug: 'handleTrackAtom audio success',
        },
      });
    } catch (_e) {
      console.error('AudioLevelGetter start failed');
      pushEvent({
        eventType: EVENT_TYPE.VIDEO_CHAT_DEBUG,
        eventName: EVENT_NAME.DEBUG__MATCH_LOGGING,
        eventParams: {
          debug: 'handleTrackAtom audio failed',
          error: _e,
        },
      });
    }
  } else {
    const peerVideoEls = get(peerVideoElsAtom);
    const hasPeerVideo = peerVideoEls.length > 0;
    pushEvent({
      eventType: EVENT_TYPE.VIDEO_CHAT_DEBUG,
      eventName: EVENT_NAME.DEBUG__MATCH_LOGGING,
      eventParams: {
        debug: 'handleTrackAtom video',
        hasPeerVideo,
      },
    });

    if (!hasPeerVideo) {
      return;
    }
    const tmpVideo = document.createElement('video');
    el = tmpVideo;

    tmpVideo.addEventListener('loadeddata', async () => {
      pushEvent({
        eventType: EVENT_TYPE.VIDEO_CHAT_DEBUG,
        eventName: EVENT_NAME.DEBUG__MATCH_LOGGING,
        eventParams: {
          debug: 'handleTrackAtom video loadeddata',
        },
      });

      // 비동기 미디어 로드 사이에 해당 매치가 종료된 경우 (페이지 이동 등 포함)
      if (matchId !== get(matchInfoAtom)?.matchId) {
        detachMediaElement(tmpVideo);
        return;
      }

      const peerVideo = new MediaStreamPipeManager();
      peerVideo.videoFpsSource = 'peer';
      peerVideo.setEl(tmpVideo, peerVideoEls);

      peerVideo.pipe(new RotateMediaStream(270, false));

      const humanVisionMediaStream = await set(startHumanVisionAtom, {
        onResult: (hvResult) => {
          if (matchId !== get(matchInfoAtom)?.matchId) return;

          set(hvmInferenceQueueAtom, (prev) => [
            ...prev,
            {
              modelStateInitialized: false,
              inferenceAt: Date.now(),
              latencyMs: Math.round(hvResult.time),
              inference: hvResult.result.map((v) => v.toExponential(3)),
              featureIds: hvResult.triggeredFeatureTypes.map(String),
            },
          ]);
          set(hvmInferenceCountAtom, (prev) => prev + 1);
          set(hvmLatencySumAtom, (prev) => prev + hvResult.time);
        },
        onTrigger: async ({ result, triggeredFeatures }) => {
          if (matchId !== get(matchInfoAtom)?.matchId) return;

          // onResult에서 count를 증가시키기 전에, trigger가 발생한 inference index를 고정
          const inferenceIndex = get(hvmInferenceCountAtom) + 1;
          const featuresToCapture = triggeredFeatures.filter(({ featureType, score }) => {
            const existing = get(hvmFeaturesCapturedAtom)[featureType];
            // Web 스펙: 기존 캡쳐보다 score가 높을 때만 이미지 갱신 (첫 trigger는 항상 저장)
            return !existing || score > existing.score;
          });

          if (featuresToCapture.length > 0) {
            try {
              const file = await humanVisionMediaStream?.captureLastInferenceFrame();

              // await 중 더 높은 score가 먼저 저장됐을 수 있으므로 재비교
              if (file && matchId === get(matchInfoAtom)?.matchId) {
                const triggeredAt = Date.now();
                set(hvmFeaturesCapturedAtom, (prev) => {
                  let hasUpdate = false;
                  const next = { ...prev };

                  featuresToCapture.forEach(({ featureType, score }) => {
                    const latest = prev[featureType];
                    if (latest && latest.score >= score) {
                      return;
                    }

                    hasUpdate = true;
                    next[featureType] = {
                      file,
                      score,
                      result,
                      triggeredAt,
                      inferenceIndex,
                    };
                  });

                  return hasUpdate ? next : prev;
                });
              }
            } catch {
              // 캡쳐 실패 시 이미지 없이 진행 — underageAutoReportAtoms에서 file: null 허용
            }
          }

          // await 후 매치가 바뀌었으면 상태 머신 오염 방지
          if (matchId !== get(matchInfoAtom)?.matchId) return;

          // score가 낮더라도 featureType별 분기(상태 머신 등)는 항상 실행
          triggeredFeatures.forEach(({ featureType }) => {
            switch (featureType) {
              case MLFeatureType.MatchHVUnderageExistAutoReport:
                set(handleUnderageTriggerAtom).catch(() => {});
                break;
              default:
                break;
            }
          });
        },
        onInitFail: () => {
          set(invalidateAndRefetchModelAtom, MLModelType.HumanVision).catch(() => {});
        },
        onSuspend: () => {
          if (humanVisionMediaStream) {
            peerVideo.unpipe(humanVisionMediaStream);
          }
        },
      });
      if (humanVisionMediaStream) {
        set(underageTriggerContextAtom, RESET);
        peerVideo.pipe(humanVisionMediaStream);
        set(humanVisionPipeAtom, humanVisionMediaStream);
        const hvmReportIntervalMs = getReportIntervalMs(
          MLModelType.HumanVision,
          matchInfo.reportEnabledModelInfos
        );
        if (hvmReportIntervalMs != null) {
          const hvmTimerId = setInterval(() => {
            set(reportHvmInferencesAtom);
          }, hvmReportIntervalMs);
          set(hvmInferenceReportTimerAtom, hvmTimerId);
        }
      }

      const antmanMediaStream = await set(startAntmanAtom, {
        onResult: (antmanResult) => {
          // 관련된 매치가 종료된 이후에 추론 결과가 나온 경우
          if (matchInfo.matchId !== get(matchInfoAtom)?.matchId) {
            antmanMediaStream?.dispose();
            return;
          }

          const { result, time } = antmanResult;
          set(antmanResultAtom, antmanResult);

          const inference = result.map((v) => v.toExponential(4));

          set(antmanInferenceQueueAtom, (prev) => [
            ...prev,
            {
              // antman 모델은 internal state 존재하지 않기 때문에 값 고정
              modelStateInitialized: false,
              inferenceAt: new Date().getTime(),
              latencyMs: Math.round(time),
              inference,
            },
          ]);

          set(antmanInferenceCountAtom, (prev) => prev + 1);
          set(antmanLatencySumAtom, (prev) => prev + time);
        },
        onTrigger: async (featureType: MLFeatureType) => {
          // 매치 전환 직후 이전 매치로 신고되는 케이스 방어
          if (matchId !== get(matchInfoAtom)?.matchId) {
            return;
          }

          const peerVideoForAntman = peerVideoEls[0];

          if (!peerVideoForAntman) {
            return;
          }

          const image = peerVideoForAntman.toDataURL('image/png');

          switch (featureType) {
            case MLFeatureType.MatchAutoReportImage:
              if (image) {
                const file = dataURItoBlob(image);

                set(antmanFeaturesCapturedAtom, {
                  ...get(antmanFeaturesCapturedAtom),
                  [MLFeatureType.MatchAutoReportImage]: {
                    file,
                    triggeredAt: new Date().getTime(),
                    inferenceIndex: get(antmanInferenceCountAtom),
                  },
                });

                set(validateImageAndReportMatchAtom, {
                  file,
                  matchId,
                  reportType: 'VISUAL_ABUSE',
                  reportMethod: ReportMatchMethod.IN_MATCH_AUTO_UNEXPOSED,
                }).catch(() => {
                  // 자동 신고는 toast 없이 무시
                });
              }
              break;
            case MLFeatureType.MatchBlur: {
              if (image) {
                const file = dataURItoBlob(image);

                set(antmanFeaturesCapturedAtom, {
                  ...get(antmanFeaturesCapturedAtom),
                  [MLFeatureType.MatchBlur]: {
                    file,
                    triggeredAt: new Date().getTime(),
                    inferenceIndex: get(antmanInferenceCountAtom),
                    handled: false,
                  },
                });

                if (antmanMediaStream) {
                  peerVideo.unpipe(antmanMediaStream);
                }
                pushEvent({
                  eventType: EVENT_TYPE.VIDEO_CHAT,
                  eventName: EVENT_NAME.MATCHING_ACTIVATE_BLUR,
                });
              }
              break;
            }
          }
        },
        onInitFail: () => {
          set(invalidateAndRefetchModelAtom, MLModelType.Antman).catch(() => {});
        },
        onSuspend: () => {
          if (antmanMediaStream) {
            peerVideo.unpipe(antmanMediaStream);
          }
        },
      });

      if (antmanMediaStream) {
        const peerVideoForAntman = peerVideoEls[0];

        if (peerVideoForAntman) {
          peerVideo.pipe(antmanMediaStream);
          const antmanReportIntervalMs = getReportIntervalMs(
            MLModelType.Antman,
            matchInfo.reportEnabledModelInfos
          );
          if (antmanReportIntervalMs != null) {
            const timerId = setInterval(() => {
              set(reportAntmanInferenceAtom);
            }, antmanReportIntervalMs);
            set(antmanInferenceReportTimerAtom, timerId);
          }
        } else {
          Sentry.captureMessage('No peer video found when trying to initialize antman');
          antmanMediaStream.dispose();
        }
      }
      peerVideo.start();
      set(peerVideoAtom, peerVideo);

      const lidModelConfig = get(lidModelConfigAtom);
      const lidReportIntervalMs = getReportIntervalMs(
        MLModelType.LID,
        matchInfo.reportEnabledModelInfos
      );

      if (lidModelConfig) {
        const sourceStream = get(sourceStreamAtom);
        if (sourceStream) {
          const registerLidPipe = (lidPipe: LidAudioPipe) => {
            if (matchId !== get(matchInfoAtom)?.matchId) {
              lidPipe.dispose();
              return false;
            }

            set(lidPipeAtom, lidPipe);

            if (lidReportIntervalMs != null) {
              const lidTimerId = setInterval(() => {
                set(reportLidInferencesAtom);
              }, lidReportIntervalMs);
              set(lidInferenceReportTimerAtom, lidTimerId);
            }
            return true;
          };

          const startLidPipe = async (modelConfig: NonNullable<typeof lidModelConfig>) => {
            const lidPipe = await set(startLidAtom, {
              modelConfig,
              sourceStream,
              onInference: (lidResult) => {
                if (matchId !== get(matchInfoAtom)?.matchId) return;

                set(lidInferenceQueueAtom, (prev) => [
                  ...prev,
                  {
                    modelStateInitialized: true,
                    inferenceAt: Date.now(),
                    latencyMs: Math.round(lidResult.time),
                    inference: lidResult.result.map((v) => v.toExponential(4)),
                    featureIds: [String(MLFeatureType.MatchLID)],
                  },
                ]);
                set(lidInferenceCountAtom, (prev) => prev + 1);
                set(lidLatencySumAtom, (prev) => prev + lidResult.time);
              },
            });

            if (!lidPipe) {
              return false;
            }

            return registerLidPipe(lidPipe);
          };

          try {
            await startLidPipe(lidModelConfig);
          } catch (error) {
            Sentry.captureMessage(`LID pipe init failed: ${error}`);
            if (matchId !== get(matchInfoAtom)?.matchId) return;

            try {
              set(applyFallbackMLConfigAtom, MLModelType.LID);
              await set(loadStaticFallbackAtom, MLModelType.LID);
              if (matchId !== get(matchInfoAtom)?.matchId) return;

              const fallbackLidModelConfig = get(lidModelConfigAtom);
              if (!fallbackLidModelConfig) {
                return;
              }

              await startLidPipe(fallbackLidModelConfig);
            } catch (fallbackError) {
              Sentry.captureMessage(`LID static fallback pipe init failed: ${fallbackError}`);
            }
          }
        }
      }

      // Moderation: 첫 peer 프레임이 렌더링된 이후에만 캡처 시작
      const peerVideoForModeration = peerVideoEls[0];
      if (peerVideoForModeration) {
        void peerVideo.firstFrameRenderedPromise.promise.then(() => {
          if (matchId !== get(matchInfoAtom)?.matchId) {
            return;
          }

          set(startServerScreeningAtom, peerVideoForModeration);
        });
      }
    });

    set(isReceivedPeerVideoFirstFrameAtom, false);
    attachMediaElement(tmpVideo, new MediaStream([e.track]), () => {
      const matchStat = get(matchStatAtom);
      if (matchStat) {
        matchStat.gotFirstFrame();
        set(isReceivedPeerVideoFirstFrameAtom, true);
      }
    }).catch((err) => {
      pushEvent({
        eventType: EVENT_TYPE.VIDEO_CHAT_DEBUG,
        eventName: EVENT_NAME.DEBUG__MATCH_LOGGING,
        eventParams: {
          debug: 'handleTrackAtom attachMediaElement failed',
          error: err,
        },
      });
    });
    const fdMsg = {
      type: 'faceDetection',
      faceDetected: 'FACE_DETECTED',
    };
    set(stompSendAtom, JSON.stringify(fdMsg));
  }
  el.addEventListener('loadeddata', () => {
    pushEvent({
      eventType: EVENT_TYPE.VIDEO_CHAT_DEBUG,
      eventName: EVENT_NAME.DEBUG__MATCH_LOGGING,
      eventParams: {
        debug: 'el.addEventListener("loadeddata", () => {',
        matchInfo: get(matchInfoAtom),
        hasChoice,
      },
    });
    if (matchId !== get(matchInfoAtom)?.matchId) {
      el = null;
      return;
    }
    if (!hasChoice) {
      set(isMatchAcceptedAtom, true);
      // audio, video 순서 보장 안됨, 올지 안올지 모름
      set(handleStartAtom);
    }
  });
});

const handlePeerConnectionAtom = atom(null, async (get, set, matchInfo: MatchInfoMessage) => {
  set(clearMatchRetryAtom);
  const connection = new PeerConnectionClient({
    onTrackEventHandler: (...args) => set(handleTrackAtom, ...args),
    onICECandidateHandler: (...args) => set(handleIceCandidateAtom, ...args),
    onVideoCallStats: (stats) => {
      set(videoCallStatAtom, stats);
    },
    onIceConnectionStateChange: () => {
      const iceConnectionState = connection.getIceConnectionState();
      if (!iceConnectionState) {
        return;
      }
      pushEvent({
        eventType: EVENT_TYPE.VIDEO_CHAT_DEBUG,
        eventName: EVENT_NAME.DEBUG__MATCH_LOGGING,
        eventParams: {
          debug: 'onIceConnectionStateChange',
          iceConnectionState,
        },
      });
      switch (iceConnectionState) {
        case 'connected': {
          const matchStat = get(matchStatAtom);
          const iceConnectTime = matchStat?.getIceConnectTime();
          if (matchStat && !iceConnectTime) {
            matchStat.setEndIceConnectTime(Date.now());
            set(handleStartAtom);
          }
          break;
        }
        default:
          break;
      }
    },
    onConnectionStateChange: () => {
      const connectionState = connection.getConnectionState();
      pushEvent({
        eventType: EVENT_TYPE.VIDEO_CHAT_DEBUG,
        eventName: EVENT_NAME.DEBUG__MATCH_LOGGING,
        eventParams: {
          debug: 'onConnectionStateChange',
          connectionState,
        },
      });
      if (!connectionState) {
        return;
      }
      switch (connectionState) {
        case 'disconnected':
        case 'failed':
        case 'closed': {
          set(handleDisconnectAtom, { reconnect: true, finisher: false });
          break;
        }
        default:
          break;
      }
    },
  });
  pushEvent({
    eventType: EVENT_TYPE.VIDEO_CHAT_DEBUG,
    eventName: EVENT_NAME.DEBUG__MATCH_LOGGING,
    eventParams: {
      debug: 'set peerConnectionAtom',
    },
  });
  set(peerConnectionAtom, connection);
  const iceServers = await IceServerManager.getInstance().getIceServers();

  if (get(peerConnectionAtom) !== connection) {
    return;
  }

  const rtcIceServers: RTCIceServer[] = iceServers.map(({ uri, username, password }) => ({
    urls: uri,
    username,
    credential: password,
  }));
  connection.open({ iceServers: rtcIceServers });
  set(matchInfoAtom, matchInfo);
  startMatchWindow(matchInfo.matchId);
  set(clearDisplayDelayAtom);
  set(matchWaitAtom);

  const prevTimer = get(sourceFpsTimerAtom);
  if (prevTimer) clearInterval(prevTimer);
  set(
    sourceFpsTimerAtom,
    setInterval(async () => {
      const pc = get(peerConnectionAtom);
      if (!pc) return;

      try {
        const stats = await pc.getStats();
        if (!stats) return;

        stats.forEach((stat) => {
          if (
            stat.type === 'media-source' &&
            stat.kind === 'video' &&
            stat.framesPerSecond != null
          ) {
            reportSourceFps('local', stat.framesPerSecond);
          }
          if (
            stat.type === 'outbound-rtp' &&
            stat.kind === 'video' &&
            stat.framesPerSecond != null
          ) {
            reportEncodeFps(stat.framesPerSecond);
          }
          if (
            stat.type === 'inbound-rtp' &&
            stat.kind === 'video' &&
            stat.framesPerSecond != null
          ) {
            reportSourceFps('peer', stat.framesPerSecond);
          }
        });
      } catch (error) {
        if (get(peerConnectionAtom) === pc) {
          Sentry.captureException(error);
        }
      }
    }, 1000)
  );

  set(attachTracksAtom);

  try {
    await connection.setMatchVideoHeight(matchInfo.matchVideoHeight);
    await connection.setMaxVideoBandwidth(matchInfo.peerProfile.maxVideoBandwidthKbps);
    const remoteConfig = get(remoteConfigAtom);
    connection.setVideoStartBitrateMode(
      remoteConfig ? isObjectKeyTrue(remoteConfig, 'useStartBitrateControl') : false
    );
    if (remoteConfig?.startBitrateControlAlphaPercent) {
      connection.setStartBitrateControlAlpha(remoteConfig.startBitrateControlAlphaPercent / 100);
    }
    if (matchInfo.initiator) {
      pushEvent({
        eventType: EVENT_TYPE.VIDEO_CHAT_DEBUG,
        eventName: EVENT_NAME.DEBUG__MATCH_LOGGING,
        eventParams: { debug: 'makeOffer' },
      });
      const description = await connection.makeOffer();
      set(stompSendAtom, JSON.stringify(description));
    }
  } catch (err) {
    pushEvent({
      eventType: EVENT_TYPE.VIDEO_CHAT_DEBUG,
      eventName: EVENT_NAME.DEBUG__MATCH_LOGGING,
      eventParams: { debug: 'handlePeerConnectionAtom failed', error: err },
    });
    Sentry.captureException(err);
  }
});

export const handleChatMessage = atom(null, (get, set, chat: BrokerEventTextChatReceived) => {
  set(receiveChatAtom, chat);
});

export const handleContentsMatchStartAtom = atom(null, (get, set) => {
  set(chatStartDateAtom, new Date());
  set(statusAtom, STATUS.CONTENTS_MATCH);

  const contentsMatchId = get(contentsMatchConfigAtom)?.contentsMatchId;

  if (contentsMatchId) {
    reportContentsMatchStartAPI({
      contentsMatchId,
    });
  }
});

const loadContentsMatchAtom = atom(null, (get, set, contentsMatchConfig: ContentsMatchMessage) => {
  set(clearMatchRetryAtom);
  set(contentsMatchConfigAtom, contentsMatchConfig);
  set(statusAtom, STATUS.CONNECTING);

  switch (contentsMatchConfig.contentsMatchMediaInfo.mediaType) {
    case 'STARTER_PACKAGE_NUDGE':
      set(showModalAtom, {
        key: ModalType.STARTER_PACKAGE_NUDGING,
        component: MatchStarterPackageNudgingModal,
      });
      break;
    default:
      // invalid contents match
      set(handleDisconnectAtom, {
        reconnect: true,
        finisher: true,
      });
      break;
  }
});

export const handleMatchStompMessageAtom = atom(
  null,
  async (get, set, { message, channelId }: { message: IMessage; channelId: string }) => {
    if (!message.body) {
      return;
    }
    const signalingInfo = get(signalingInfoAtom);
    if (!signalingInfo || signalingInfo.channelId !== channelId) {
      return;
    }

    pushEvent({
      eventType: EVENT_TYPE.VIDEO_CHAT,
      eventName: EVENT_NAME.MATCH_RECEIVE__STOMP_MESSAGE,
      eventParams: { body: message.body },
    });
    if (message.body === 'BYE') {
      set(handleDisconnectAtom, {
        reconnect: get(statusAtom) >= STATUS.FINDING,
        finisher: false,
      });
      return;
    }

    const peerConnection = get(peerConnectionAtom);

    const brokerMessage = JSON.parse(message.body) as MatchBrokerMessage;
    switch (brokerMessage.type) {
      case 'contentsMatchMessage': {
        set(loadContentsMatchAtom, brokerMessage);
        break;
      }
      case 'cameraStatusUpdated': {
        set(peerCameraEnabledAtom, brokerMessage.enabled);
        const peerAudioLevelGetter = get(peerAudioLevelGetterAtom);
        if (peerAudioLevelGetter) {
          if (brokerMessage.enabled) {
            peerAudioLevelGetter.pause();
          } else {
            peerAudioLevelGetter.start();
          }
        }
        break;
      }
      case 'matchToLiveRoom':
        set(handleDisconnectAtom, { reconnect: true, finisher: false });
        return;
      case 'matchInfo':
        pushEvent({
          eventType: EVENT_TYPE.VIDEO_CHAT_DEBUG,
          eventName: EVENT_NAME.DEBUG__MATCH_LOGGING,
          eventParams: {
            debug: 'receive matchInfo',
            matchInfo: brokerMessage,
            hasPeerConnection: !!peerConnection,
          },
        });
        if (!peerConnection) {
          const matchStat = get(matchStatAtom);
          if (matchStat != null) {
            const now = Date.now();
            matchStat.endProfileTime = now;
            matchStat.beginIceConnectTime = now;
          }
          set(handlePeerConnectionAtom, brokerMessage);
        } else {
          Sentry.captureMessage('received matchInfo while connection exist', {
            extra: {
              matchInfo: brokerMessage,
              hasPeerConnection: !!peerConnection,
              connectionState: peerConnection.getConnectionState(),
              signalingState: peerConnection.getSignalingState(),
            },
          });
        }
        break;
      case 'offer': {
        try {
          await peerConnection?.setRemoteDescription(brokerMessage);
          await peerConnection
            ?.makeAnswer()
            .then((description) => set(stompSendAtom, JSON.stringify(description)));
        } catch (_e) {
          set(handleDisconnectAtom, { reconnect: true, finisher: false });
          return;
        }
        break;
      }
      case 'answer':
        try {
          await peerConnection?.setRemoteDescription(brokerMessage);
        } catch (_e) {
          set(handleDisconnectAtom, { reconnect: true, finisher: false });
          return;
        }
        break;
      case 'candidate': {
        try {
          await peerConnection?.addIceCandidate(brokerMessage);
        } catch (_e) {
          set(handleDisconnectAtom, { reconnect: true, finisher: false });
          return;
        }
        break;
      }
      // TODO: 이거 받아야 함
      case 'matchAccepted': {
        // ios easy connect 꺼져있을 때, 즉 hasChoice == true 일 때 matchAccepted 받아야 status -> matched로 넘어감
        set(isMatchAcceptedAtom, true);
        set(handleStartAtom);
        break;
      }
      case 'systemMessageInMatch': {
        set(chatListAtom, (prev) => {
          return prev.concat([
            {
              type: 'system_message',
              messageUid: brokerMessage.id,
              message: formatEmojiText(brokerMessage.message),
              iconUrl: brokerMessage.iconUrl,
            },
          ]);
        });
        break;
      }
      case 'chatMsg': {
        set(handleChatMessage, brokerMessage);
        break;
      }
      case 'recordingConsentRequested': {
        const matchInfo = get(matchInfoAtom);

        if (matchInfo?.matchId !== brokerMessage.matchId) return;

        set(chatListAtom, (prev) => {
          const alreadyRequested = prev.some((chat) => chat.type === 'recordingConsentRequested');
          if (alreadyRequested) return prev;

          return prev.concat([
            {
              type: 'recordingConsentRequested',
              messageUid: `${Date.now()}`,
            },
          ]);
        });
        break;
      }
      case 'recordingConsentUpdated': {
        const matchInfo = get(matchInfoAtom);
        if (matchInfo?.matchId !== brokerMessage.matchId) return;

        set(chatListAtom, (prev) =>
          prev.concat([
            {
              type: 'recordingConsentUpdated',
              messageUid: `${Date.now()}`,
              isAccepted: brokerMessage.accepted,
            },
          ])
        );
        break;
      }
      case 'pranswer':
      case 'rollback':
        break;
      case 'typingInfo':
        set(isPeerTypingAtom, brokerMessage.typing);
        break;
      case 'friendAccepted':
        get(queryClientAtom).invalidateQueries({
          queryKey: FOLLOW_QUERY_KEYS.follow(),
        });
        break;
      case 'ChildDetected': {
        set(handleChildDetectedReceivedAtom, {
          brokerMessage,
          requestId: get(matchConfigAtom)?.requestId,
        });
        break;
      }
      case 'ScreeningEvent': {
        if (brokerMessage.role === 'OFFENDER' && brokerMessage.phase === 'DETECTED') {
          set(handleChildDetectedReceivedAtom, {
            brokerMessage,
            requestId: get(matchConfigAtom)?.requestId,
          });
        } else if (brokerMessage.role === 'VICTIM' && brokerMessage.phase === 'DETECTED') {
          const currentMatchId = get(matchInfoAtom)?.matchId;
          if (brokerMessage.matchId === currentMatchId) {
            set(applyServerScreeningCooldownAtom, {
              matchId: brokerMessage.matchId,
              cooldownMs: brokerMessage.cooldownMs,
            });
            set(hvsScreeningCaptureAtom, {
              matchId: brokerMessage.matchId,
              fileInfo: brokerMessage.fileInfo ?? undefined,
            });
          }
        } else if (brokerMessage.role === 'VICTIM' && brokerMessage.phase === 'REPORTED') {
          set(handleScreeningReportedAtom, brokerMessage);
        }
        break;
      }
      case 'friendRequested': {
        const matchInfo = get(matchInfoAtom);
        if (matchInfo?.matchId !== brokerMessage.matchId) return;

        set(chatListAtom, (prev) => [
          ...prev,
          {
            type: 'following_message',
            messageUid: `following-${Date.now()}`,
            messageKey: 'match.toast_follow',
            iconUrl: getChatEventIcon('FOLLOWING'),
          },
        ]);
        break;
      }
    }
  }
);

export type DesktopLayoutType = 'WIDE' | 'SHORTS' | 'COMBINED';

export const creatorDesktopLayoutAtom = getAtomWithStorage<DesktopLayoutType>(
  'desktopLayout',
  'WIDE'
);

/**
 * 데스크탑 모드 커스터마이징은 크리에이터 모드에서만 가능
 */
export const desktopLayoutAtom = atom<DesktopLayoutType>((get) => {
  const isCreatorMode = get(isCreatorModeAtom);
  return isCreatorMode ? get(creatorDesktopLayoutAtom) : 'WIDE';
});

export const creatorSelectedViewModeEventNameAtom = atom((get) =>
  getCreatorViewModeEventName(get(creatorDesktopLayoutAtom))
);

const mobileLayoutStorageAtom = getAtomWithStorage<'DEFAULT' | 'HALF' | null>('mobileLayout', null);

export const mobileLayoutAtom = atom(
  (get) => {
    const storageValue = get(mobileLayoutStorageAtom);
    if (storageValue !== null) return storageValue;

    const remoteConfig = get(remoteConfigAtom);
    if (remoteConfig) {
      return isObjectKeyTrue(remoteConfig, 'splitViewUi') ? 'HALF' : 'DEFAULT';
    }
    return 'DEFAULT';
  },
  (get, set, newValue: 'DEFAULT' | 'HALF' | null) => {
    set(mobileLayoutStorageAtom, newValue);
  }
);

export const toggleVideoTracksAtom = atom(null, async (get, set) => {
  const localVideoStream = get(localVideoStreamAtom);
  const videoTracks = localVideoStream?.getVideoTracks();
  const localAudioLevelGetter = get(localAudioLevelGetterAtom);
  if (videoTracks && videoTracks.length > 0) {
    const videoTrack = videoTracks[0];
    videoTrack.enabled = false;
    videoTrack.stop();
    localVideoStream?.removeTrack(videoTrack);
    localAudioLevelGetter?.start();
    set(isOnCameraAtom, false);
  } else {
    set(isOnCameraAtom, true);
    await set(grantVideoAtom);
    localAudioLevelGetter?.pause();
  }
  set(sendStompCameraStatusUpdatedAtom);
});

export const isVideoObjectFitCoverAtom = getAtomWithStorage<boolean>('isVideoObjectFitCover', true);

export const pushMatchViewModeEvent = atom(
  null,
  (get, set, { matchId, chatDurationMs }: { matchId: string; chatDurationMs?: number }) => {
    const width = window.innerWidth > 1280 ? 'long' : 'short';
    const mobileLayout = get(mobileLayoutAtom);
    const isVideoObjectFitCover = get(isVideoObjectFitCoverAtom);
    const isCreator = get(isCreatorModeAtom);

    pushEvent({
      eventName: EVENT_NAME.MATCH__RESULT_VIEWMODE,
      eventType: EVENT_TYPE.VIEW,
      eventParams: {
        action_category: 'action',
        tab: 'mirror',
        page: 'match',
        target: 'result_viewmode',
        width,
        letterbox: isVideoObjectFitCover ? 'off' : 'on',
        match_id: matchId,
        chat_duration_ms: chatDurationMs,
        ...(width === 'short' && {
          split: mobileLayout === 'DEFAULT' ? 'off' : 'on',
        }),
        is_creator: isCreator,
        view_mode: isCreator ? get(creatorSelectedViewModeEventNameAtom) : false, // creator가 아닐 경우 da 에서 false로 요청함
      },
    });
  }
);

export const eventMatchPageAtom = atom((get) => {
  const status = get(statusAtom);
  switch (status) {
    case STATUS.INITIAL:
      return 'main';
    case STATUS.FINDING:
      return 'finding';
    case STATUS.CONNECTING:
      return 'connecting';
    case STATUS.CONTENTS_MATCH:
      return 'teammatch'; // 컨텐츠 매치 = 팀 매치 (동일 개념이지만 레거시로 용어 혼재)
    default:
      return 'match';
  }
});
